import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {loadCurrentWasmDevice, WASM_DEVICE_SAVED_EVENT} from "../../wasm/deviceStore";
import type {CreateTeamResult, AddUserToTeamResult, TeamPermission, UpdateMemberPermissionsResult} from "./useDevice.types";
import type {Device, ListSavesArgs as ListSavesArgsType, ListSavesResult as ListSavesResultType} from "./useDevice.types";
import type {RotatePukResult} from "./useDevice.types";
import type {
    ReadSaveE2EResult,
    WriteSaveResult,
    ReadSendResult,
    WriteSendResult,
} from "./useDevice.types";
import type {EncryptOutput} from "../../crypto";
import {Team} from "../../misc/Team";
import type {PreparedPaperKey, PreparedRecoveryDevice, RecoveryGroupEntry, ProvisionDeviceResult, PendingAccount, CompleteUserCreationResult, RemoteDeviceInfo, ProgressCallback} from "./useDevice.types";
import {
    readSave as wasmReadSave,
    writeSave as wasmWriteSave,
    listSaves as wasmListSaves,
    deleteSave as wasmDeleteSave,
    rotatePuk as wasmRotatePuk,
    listDevices as wasmListDevices,
    revokeDevices as wasmRevokeDevices,
    pushKeys as wasmPushKeys,
    pullKeyring as wasmPullKeyring,
    decryptX25519Envelope as wasmDecryptX25519Envelope,
    encryptWithDeviceAes as wasmEncryptWithDeviceAes,
    decryptEncryptedItem as wasmDecryptEncryptedItem,
    provisionWithAuthorizer as wasmProvisionWithAuthorizer,
    provisionWithPairing as wasmProvisionWithPairing,
    intendToAddPaymentAuthorization as wasmIntendToAddPaymentAuthorization,
    getPaymentAuthorizationPdf as wasmGetPaymentAuthorizationPdf,
    addPaymentMethod as wasmAddPaymentMethod,
    storeEphemeralIdentity as wasmStoreEphemeralIdentity,
    getUserInfo as wasmGetUserInfo,
    getAdminStatus as wasmGetAdminStatus,
    adminListSubscriptions as wasmAdminListSubscriptions,
    adminProvisionSubscription as wasmAdminProvisionSubscription,
    adminAssignLicense as wasmAdminAssignLicense,
    adminUnassignLicense as wasmAdminUnassignLicense,
    adminSearchUsers as wasmAdminSearchUsers,
    adminGetIdentityDetail as wasmAdminGetIdentityDetail,
    adminDeleteSubscription as wasmAdminDeleteSubscription,
    listManagedProviderProfiles as wasmListManagedProviderProfiles,
    listAllProviderProfiles as wasmListAllProviderProfiles,
    hasProviderProfileManageAccess as wasmHasProviderProfileManageAccess,
    hasReferralPage as wasmHasReferralPage,
    listMyManagedProviderProfiles as wasmListMyManagedProviderProfiles,
    listMyProviderProfileChanges as wasmListMyProviderProfileChanges,
    submitProviderProfileChange as wasmSubmitProviderProfileChange,
    resubmitProviderProfileChange as wasmResubmitProviderProfileChange,
    adminListProviderProfileChanges as wasmAdminListProviderProfileChanges,
    adminApproveProviderProfileChange as wasmAdminApproveProviderProfileChange,
    adminRejectProviderProfileChange as wasmAdminRejectProviderProfileChange,
    reassignProviderProfileRecipient as wasmReassignProviderProfileRecipient,
    adminListOpenDemand as wasmAdminListOpenDemand,
    adminListOnboardingRequests as wasmAdminListOnboardingRequests,
    adminListRecentAccounts as wasmAdminListRecentAccounts,
    adminGetOnboardingRequest as wasmAdminGetOnboardingRequest,
    adminMarkOnboardingFollowedUp as wasmAdminMarkOnboardingFollowedUp,
    registerPushToken as wasmRegisterPushToken,
    revokePushToken as wasmRevokePushToken,
    listMyPushTokens as wasmListMyPushTokens,
    pushPublicKey as wasmPushPublicKey,
    adminListInterestSubmissions as wasmAdminListInterestSubmissions,
    adminApproveInterestSubmission as wasmAdminApproveInterestSubmission,
    adminRejectInterestSubmission as wasmAdminRejectInterestSubmission,
    adminAskStillLooking as wasmAdminAskStillLooking,
    recommendProvidersToSearch as wasmRecommendProvidersToSearch,
    demandItemsForIdentity as wasmDemandItemsForIdentity,
    respondToDemandInvitation as wasmRespondToDemandInvitation,
    resubmitDemandInterest as wasmResubmitDemandInterest,
    openDemandOutreachLinkAsSelf as wasmOpenDemandOutreachLinkAsSelf,
    submitDemandInterest as wasmSubmitDemandInterest,
    drainAvaStillLookingCheck as wasmDrainAvaStillLookingCheck,
    settleStillLookingCheck as wasmSettleStillLookingCheck,
    getDemandThread as wasmGetDemandThread,
    sendDemandMessage as wasmSendDemandMessage,
    drainAvaStaffMessages as wasmDrainAvaStaffMessages,
    sendPatientStaffMessage as wasmSendPatientStaffMessage,
    userBubbleForAnswer as wasmUserBubbleForAnswer,
    shareLinkURL as wasmShareLinkURL,
    drainAvaPending as wasmDrainAvaPending,
    setDemandMessagesRead as wasmSetDemandMessagesRead,
    adminListUnreadDemandThreads as wasmAdminListUnreadDemandThreads,
    adminGetStaffTeam as wasmAdminGetStaffTeam,
    adminDesignateStaffTeam as wasmAdminDesignateStaffTeam,
    adminCreatePayer as wasmAdminCreatePayer,
    adminUpdatePayer as wasmAdminUpdatePayer,
    adminRetirePayer as wasmAdminRetirePayer,
    adminSetAvaPayerChips as wasmAdminSetAvaPayerChips,
    adminImportPayers as wasmAdminImportPayers,
    adminPreviewPayerChange as wasmAdminPreviewPayerChange,
    adminPayerUsedBy as wasmAdminPayerUsedBy,
    adminPayersByState as wasmAdminPayersByState,
    adminPayerChangeLog as wasmAdminPayerChangeLog,
    adminPayerResearchSources as wasmAdminPayerResearchSources,
    adminResearchPayer as wasmAdminResearchPayer,
    adminRecheckPayer as wasmAdminRecheckPayer,
    adminResearchPayerAgain as wasmAdminResearchPayerAgain,
    adminPayerResearchRuns as wasmAdminPayerResearchRuns,
    adminPayerResearchRun as wasmAdminPayerResearchRun,
    adminDecidePayerFinding as wasmAdminDecidePayerFinding,
    adminLeaveOutPayerResearch as wasmAdminLeaveOutPayerResearch,
    adminGetDemandDetail as wasmAdminGetDemandDetail,
    adminDemandMatches as wasmAdminDemandMatches,
    adminMintDemandOutreachToken as wasmAdminMintDemandOutreachToken,
    setProviderProfileListed as wasmSetProviderProfileListed,
    adminRevokeDemandOutreachToken as wasmAdminRevokeDemandOutreachToken,
    getProviderProfile as wasmGetProviderProfile,
    resolveBookLink as wasmResolveBookLink,
    listPracticeProfiles as wasmListPracticeProfiles,
    createPracticeProfile as wasmCreatePracticeProfile,
    updatePracticeProfile as wasmUpdatePracticeProfile,
    deletePracticeProfile as wasmDeletePracticeProfile,
    setProviderProfilePractice as wasmSetProviderProfilePractice,
    arrangePracticeMembers as wasmArrangePracticeMembers,
    getReferenceData as wasmGetReferenceData,
    nppesLookup as wasmNppesLookup,
    adminRenderProviderPreview as wasmAdminRenderProviderPreview,
    adminMintProviderPreviewLink as wasmAdminMintProviderPreviewLink,
    adminRevokeProviderPreviewLink as wasmAdminRevokeProviderPreviewLink,
    adminListProviderPreviewLinks as wasmAdminListProviderPreviewLinks,
    createProviderProfile as wasmCreateProviderProfile,
    updateProviderProfile as wasmUpdateProviderProfile,
    deleteProviderProfile as wasmDeleteProviderProfile,
    uploadProviderAsset as wasmUploadProviderAsset,
    startAvaSearch as wasmStartAvaSearch,
    previewAvaSearch as wasmPreviewAvaSearch,
    runAvaMatch as wasmRunAvaMatch,
    drainAvaRecommendations as wasmDrainAvaRecommendations,
    consumePushFrame,
    catchUpAfterConnect,
    advanceAvaTurn as wasmAdvanceAvaTurn,
    sendInquiriesAndAdvance as wasmSendInquiriesAndAdvance,
    buildAppointmentRequestDetails as wasmBuildAppointmentRequestDetails,
    suggestAvaProviders as wasmSuggestAvaProviders,
    bindAvaSearchToAccount as wasmBindAvaSearchToAccount,
    sendAppointmentRequestInquiries as wasmSendAppointmentRequestInquiries,
    type AppointmentInquiryInput as WasmAppointmentInquiryInput,
    listAppointmentRequests as wasmListAppointmentRequests,
    type ListAppointmentRequestsArgs as WasmListAppointmentRequestsArgs,
    makeAppointmentOffer as wasmMakeAppointmentOffer,
    type MakeOfferInput as WasmMakeOfferInput,
    acceptAppointmentOffer as wasmAcceptAppointmentOffer,
    recordAppointmentOutcome as wasmRecordAppointmentOutcome,
    rateProvider as wasmRateProvider,
    ratePatient as wasmRatePatient,
    getMyPatientReputation as wasmGetMyPatientReputation,
    ratingsEnabled as wasmRatingsEnabled,
    declineAppointmentRequest as wasmDeclineAppointmentRequest,
    setAppointmentMessagesRead as wasmSetAppointmentMessagesRead,
    requestAppointmentForms as wasmRequestAppointmentForms,
    recordAppointmentFormSubmission as wasmRecordAppointmentFormSubmission,
    declineAppointmentOffer as wasmDeclineAppointmentOffer,
    withdrawAppointmentOffer as wasmWithdrawAppointmentOffer,
    sendAppointmentMessage as wasmSendAppointmentMessage,
    deleteAppointmentSearch as wasmDeleteAppointmentSearch,
    archiveAppointmentSearch as wasmArchiveAppointmentSearch,
    unarchiveAppointmentSearch as wasmUnarchiveAppointmentSearch,
    withdrawAppointmentSearch as wasmWithdrawAppointmentSearch,
    getAppointmentThread as wasmGetAppointmentThread,
    hasAppointmentRecipientAccess as wasmHasAppointmentRecipientAccess,
    hasTeamMembership as wasmHasTeamMembership,
    listMyAppointmentSearches as wasmListMyAppointmentSearches,
    listMyRequestInquiries as wasmListMyRequestInquiries,
    decryptSearchState as wasmDecryptSearchState,
    getAvaTranscript as wasmGetAvaTranscript,
    getSavedForm as wasmGetSavedForm,
    listPaymentVendors as wasmListPaymentVendors,
    getPaymentVendor as wasmGetPaymentVendor,
    deletePaymentVendor as wasmDeletePaymentVendor,
    validatePaymentVendor as wasmValidatePaymentVendor,
    createSquareOAuthUrl as wasmCreateSquareOAuthUrl,
    completeSquareOAuth as wasmCompleteSquareOAuth,
    listPlans as wasmListPlans,
    getMyLicenses as wasmGetMyLicenses,
    getMyUsage as wasmGetMyUsage,
    subscribeMe as wasmSubscribeMe,
    changeSubscriptionPlan as wasmChangeSubscriptionPlan,
    cancelSubscription as wasmCancelSubscription,
    reactivateSubscription as wasmReactivateSubscription,
    createFormTemplate as wasmCreateFormTemplate,
    updateFormTemplate as wasmUpdateFormTemplate,
    updateFormTemplateName as wasmUpdateFormTemplateName,
    updateFormTemplateMarks as wasmUpdateFormTemplateMarks,
    updateFormTemplatePDF as wasmUpdateFormTemplatePDF,
    updateFormTemplateAllowAnonymous as wasmUpdateFormTemplateAllowAnonymous,
    updateFormTemplateRecipient as wasmUpdateFormTemplateRecipient,
    deleteFormTemplate as wasmDeleteFormTemplate,
    claimFormTemplate as wasmClaimFormTemplate,
    listFormTemplates as wasmListFormTemplates,
    readFormTemplatePdf as wasmReadFormTemplatePdf,
    assignPaymentIntegrationToTemplate as wasmAssignPaymentIntegrationToTemplate,
    createTeam as wasmCreateTeam,
    addUserToTeam as wasmAddUserToTeam,
    updateMemberPermissions as wasmUpdateMemberPermissions,
    removeMemberFromTeam as wasmRemoveMemberFromTeam,
    deleteTeam as wasmDeleteTeam,
    listTeams as wasmListTeams,
    listTeamMembers as wasmListTeamMembers,
    inviteExistingUserToTeam as wasmInviteExistingUserToTeam,
    acceptTeamInvitation as wasmAcceptTeamInvitation,
    declineTeamInvitation as wasmDeclineTeamInvitation,
    withdrawTeamInvitation as wasmWithdrawTeamInvitation,
    requestToJoinTeam as wasmRequestToJoinTeam,
    approveJoinRequest as wasmApproveJoinRequest,
    denyJoinRequest as wasmDenyJoinRequest,
    listTeamJoinRequests as wasmListTeamJoinRequests,
    inviteEmailToTeam as wasmInviteEmailToTeam,
    claimEmailInvitation as wasmClaimEmailInvitation,
    listPendingIbms as wasmListPendingIbms,
    notificationCenterItems as wasmNotificationCenterItems,
    notificationUnreadCount as wasmNotificationUnreadCount,
    dismissStateItem as wasmDismissStateItem,
    listTeamPendingInvitations as wasmListTeamPendingInvitations,
    handlePtkRotations as wasmHandlePtkRotations,
    checkPairingStatus as wasmCheckPairingStatus,
    approvePairing as wasmApprovePairing,
    createUser as wasmCreateUser,
    completeUserCreation as wasmCompleteUserCreation,
    verifyCreationCode as wasmVerifyCreationCode,
    provePaperKeyWithWords as wasmProvePaperKeyWithWords,
    finalizeUserCreation as wasmFinalizeUserCreation,
    preparePaperKey as wasmPreparePaperKey,
    registerPaperKey as wasmRegisterPaperKey,
    prepareRecoveryDevice as wasmPrepareRecoveryDevice,
    wrapRecoverySeedForPasskey as wasmWrapRecoverySeedForPasskey,
    unwrapRecoverySeedWithPasskey as wasmUnwrapRecoverySeedWithPasskey,
    registerPasskeyDevice as wasmRegisterPasskeyDevice,
    recoveryEnvelope as wasmRecoveryEnvelope,
    generatePaperKey as wasmGeneratePaperKey,
    bindAnonymousFormSubmission as wasmBindAnonymousFormSubmission,
    writeSend as wasmWriteSend,
    writeSendAnonymous as wasmWriteSendAnonymous,
    writeSendMulti as wasmWriteSendMulti,
    readSend as wasmReadSend,
    readSendAnonymous as wasmReadSendAnonymous,
    claimSendAccess as wasmClaimSendAccess,
    updateRecipientState as wasmUpdateRecipientState,
    updateUserinfo as wasmUpdateUserinfo,
    requestEphemeralEmailVerification as wasmRequestEphemeralEmailVerification,
    verifyEphemeralAccess as wasmVerifyEphemeralAccess,
    sendEphemeralAccessEmail as wasmSendEphemeralAccessEmail,
    listSends as wasmListSends,
    getIdentity as wasmGetIdentity,
    getIdentities as wasmGetIdentities,
    sendHeartbeat as wasmSendHeartbeat,
    adminListReferrers as wasmAdminListReferrers,
    adminGetReferrer as wasmAdminGetReferrer,
    adminCreateReferrer as wasmAdminCreateReferrer,
    adminUpdateReferrer as wasmAdminUpdateReferrer,
    adminCreateReferralPair as wasmAdminCreateReferralPair,
    adminSetReferralRank as wasmAdminSetReferralRank,
    adminRevokeReferralLink as wasmAdminRevokeReferralLink,
    adminDeleteReferrer as wasmAdminDeleteReferrer,
    adminRecordReferrerTouch as wasmAdminRecordReferrerTouch,
    adminSetReferrerRecruiting as wasmAdminSetReferrerRecruiting,
    adminListReferralClinicians as wasmAdminListReferralClinicians,
    adminGetReferralClinician as wasmAdminGetReferralClinician,
    adminListReferralTargets as wasmAdminListReferralTargets,
    adminGetReferralPractice as wasmAdminGetReferralPractice,
    getReferralPage as wasmGetReferralPage,
    revokeReferralPair as wasmRevokeReferralPair,
    referralShareAs as wasmReferralShareAs,
    shareReferralCode as wasmShareReferralCode,
    attachSharedCodes as wasmAttachSharedCodes,
    adminMakeReferralCode as wasmAdminMakeReferralCode,
    adminFindAccounts as wasmAdminFindAccounts,
    adminLinkReferrerAccount as wasmAdminLinkReferrerAccount,
    setUnlockPrompter,
    setSetupPrompter,
    NoWasmDeviceError,
} from "../../wasm/ops";
import type {PushSubscriptionArgs} from "../../wasm/ops";
import {ReUnlockDialog} from "../../wasm/ReUnlockDialog";
import {DeviceSetupOverlay} from "../../wasm/DeviceSetupOverlay";
import {startPush, PUSH_FRAME_EVENT, PUSH_CONNECTED_EVENT} from "../../wasm/pushConnection";

// Helper to invalidate dashboard teams cache after team changes
function invalidateLocalCacheForTeams() {
    try {
        localStorage.removeItem('MyFormsPageTeams');
    } catch (e) {
        console.warn('[useDevice] Failed to invalidate teams cache:', e);
    }
}

/**
 * User info including identity keys
 * Populated from getUserinfo (user details) and getIdentity (crypto keys)
 */
export interface UserInfo {
    // From getUserinfo - user details
    id: string;
    name?: string;
    given_name?: string;
    family_name?: string;
    email?: string;
    email_verified?: boolean;
    phone?: string;
    // From getIdentity - user's crypto key IDs
    seedKid?: string;
    sigKid?: string;
    encKid?: string;
    aesKid?: string;
}

/**
 * Result of team deletion
 */
export interface DeleteTeamResult {
    success: boolean;
    error?: string;
}

// Re-export team-related types for convenience
export type { AddUserToTeamResult, TeamPermission, UpdateMemberPermissionsResult } from "./useDevice.types";

/**
 * Result of removing a member from team
 */
export interface RemoveMemberResult {
  success: boolean;
  error?: string;
}

// Re-export Device from types file to ensure single source of truth
export type { Device } from "./useDevice.types";

// Re-export send result types for convenience
export type { WriteSendResult, ReadSendResult } from "./useDevice.types";
export type { WriteSaveResult, ReadSaveE2EResult } from "./useDevice.types";
export type { RotatePukResult } from "./useDevice.types";

/**
 * useDevice Hook
 *
 * Manages device identity.
 *
 * Features:
 * - Loads device keys on mount
 * - Refreshes userInfo event-driven (on push + reconnect), not on a timer
 * - Exposes a manual heartbeat trigger (non-WebSocket fallback)
 *
 * @returns Device interface with kids, identity functions, and state
 */
export const useDevice = (): Device => {
    const [isReady, setIsReady] = useState<boolean>(false);
    const [encKid, setEncKid] = useState<string | null>(null);
    const [sigKid, setSigKid] = useState<string | null>(null);
    const [seedKid, setSeedKid] = useState<string | null>(null);
    const [nickname, setNickname] = useState<string | null>(null);
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [userInfoSettled, setUserInfoSettled] = useState<boolean>(false);
    const lastSeenAtRef = useRef<string | null>(null);

    // Heartbeat function — routes through the WASM device. Skip
    // silently when no WasmDevice is provisioned yet: the desire to
    // heartbeat shouldn't be the thing that triggers the setup
    // prompter. Real user-driven ops will surface setup when they
    // need it; once a device exists, the next tick picks it up.
    const sendHeartbeat = useCallback(async () => {
        if (!(await loadCurrentWasmDevice())) return;
        try {
            const response = await wasmSendHeartbeat();

            if (response.success) {
                // console.log('Heartbeat sent successfully', {
                //     lastSeen: response.lastSeenAt,
                //     serverTime: response.serverTime,
                //     timeSince: response.timeSinceLastSeen,
                // });

                // Update last seen timestamp (ref to avoid re-renders)
                if (response.lastSeenAt) {
                    lastSeenAtRef.current = response.lastSeenAt;
                }

                applyUserInfo(response.user as unknown as UserInfo)
            } else {
                console.error('Heartbeat failed:', response.error);
            }
        } catch (error) {
            console.error('Error sending heartbeat:', error);
        }
    }, []);

    // Store userInfo in ref so createTeam can access latest value
    const userInfoRef = useRef<UserInfo | null>(null);
    userInfoRef.current = userInfo;

    // Apply a fresh UserInfo, skipping the state update (and re-render)
    // when nothing the UI reads has changed. Shared by sendHeartbeat and
    // the event-driven refreshUserInfo.
    const applyUserInfo = useCallback((next: UserInfo | null | undefined) => {
        if (!next) return;
        const cur = userInfoRef.current;
        const changed =
            cur?.aesKid         !== next.aesKid         ||
            cur?.email          !== next.email          ||
            cur?.email_verified !== next.email_verified ||
            cur?.encKid         !== next.encKid         ||
            cur?.family_name    !== next.family_name    ||
            cur?.given_name     !== next.given_name     ||
            cur?.id             !== next.id             ||
            cur?.name           !== next.name           ||
            cur?.nickname       !== next.nickname       ||
            cur?.seedKid        !== next.seedKid        ||
            cur?.sigKid         !== next.sigKid;
        if (changed) {
            userInfoRef.current = next;
            setUserInfo(next);
        }
    }, []);

    const pullKeyring = useCallback(async () => {
        try { return await wasmPullKeyring(); }
        catch (e) {
            console.warn('[pullKeyring] error:', e);
            return { added: 0, skipped: 0, total: 0 };
        }
    }, []);

    const decryptX25519Envelope = useCallback(async (envelope: unknown): Promise<Uint8Array> => {
        return await wasmDecryptX25519Envelope(envelope);
    }, []);

    const provisionWithAuthorizer = useCallback(async (
        authorizerSeedHex: string,
        deviceName: string,
        deviceType: 'regular' | 'paper' = 'regular',
    ) => {
        try { return await wasmProvisionWithAuthorizer(authorizerSeedHex, deviceName, deviceType); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);
    const provisionWithPairing = useCallback(async (deviceName?: string) => {
        try { return await wasmProvisionWithPairing(deviceName); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);

    const intendToAddPaymentAuthorization = useCallback(async (opts: { practice?: string; integrationId?: string }) => {
        return await wasmIntendToAddPaymentAuthorization(opts);
    }, []);
    const getPaymentAuthorizationPdf = useCallback(async (id: string) => {
        return await wasmGetPaymentAuthorizationPdf(id);
    }, []);
    const addPaymentMethod = useCallback(async (args: {
        practice?: string;
        integrationId?: string;
        paymentMethod?: string;
        billingPostalCode?: string;
        formData?: Record<string, any>;
    }) => {
        return await wasmAddPaymentMethod(args);
    }, []);
    const storeEphemeralIdentity = useCallback(async (fragment: string) => {
        return await wasmStoreEphemeralIdentity(fragment);
    }, []);
    const getUserInfo = useCallback(async () => {
        return (await wasmGetUserInfo()) as unknown as UserInfo;
    }, []);
    // Event-driven userInfo refresh — replaces the retired heartbeat
    // poll. Re-pulls authoritative UserInfo via the canonical getUserInfo
    // query (itself a device-signed request, so it also advances
    // last_seen_at server-side). Invoked on push (puk_rotated) and on
    // socket (re)connect; see Push-Architecture §16.5.
    const refreshUserInfo = useCallback(async (): Promise<UserInfo | null> => {
        try {
            const info = await getUserInfo();
            applyUserInfo(info);
            return info ?? null;
        } catch (err) {
            console.warn('[useDevice] refreshUserInfo failed (non-fatal):', err);
            return null;
        }
    }, [getUserInfo, applyUserInfo]);
    const getAdminStatus = useCallback(async () => {
        try { return await wasmGetAdminStatus(); }
        catch (e) { console.warn('[getAdminStatus]', e); return { isAdmin: false }; }
    }, []);
    const adminListSubscriptions = useCallback(async (args?: { planId?: string; status?: string; limit?: number; offset?: number }) => {
        return await wasmAdminListSubscriptions(args);
    }, []);
    const adminProvisionSubscription = useCallback(async (userId: string, planId: string, quantity?: number) => {
        return await wasmAdminProvisionSubscription(userId, planId, quantity);
    }, []);
    const adminAssignLicense = useCallback(async (licenseId: string, userId: string) => {
        return await wasmAdminAssignLicense(licenseId, userId);
    }, []);
    const adminUnassignLicense = useCallback(async (licenseId: string) => {
        return await wasmAdminUnassignLicense(licenseId);
    }, []);
    const adminSearchUsers = useCallback(async (searchTerm: string, limit?: number) => {
        return await wasmAdminSearchUsers(searchTerm, limit);
    }, []);
    const adminGetIdentityDetail = useCallback(async (id: string) => {
        return await wasmAdminGetIdentityDetail(id);
    }, []);
    const adminDeleteSubscription = useCallback(async (subscriptionId: string) => {
        return await wasmAdminDeleteSubscription(subscriptionId);
    }, []);
    // --- Provider profiles (booking marketplace, §5.18/§16) ----------
    // PROPAGATE failures (mirrors listMyAppointmentSearches): swallowing to []
    // makes a transient auth/'unknown device' error look like the user genuinely
    // has no profiles. The admin provider-profiles surface its own error. (#16)
    const listManagedProviderProfiles = useCallback(async (identityId: string) => {
        return await wasmListManagedProviderProfiles(identityId);
    }, []);
    const listAllProviderProfiles = useCallback(async () => {
        return await wasmListAllProviderProfiles();
    }, []);
    // Vetted provider-profile proposals. Pass-throughs; every gate and rule is
    // server-side. Errors PROPAGATE — both surfaces (the author's proposals
    // page and the admin queue) must show a real failure, not an empty state.
    const hasProviderProfileManageAccess = useCallback(async () => {
        return await wasmHasProviderProfileManageAccess();
    }, []);
    const hasReferralPage = useCallback(async () => {
        return await wasmHasReferralPage();
    }, []);
    const listMyManagedProviderProfiles = useCallback(async () => {
        return await wasmListMyManagedProviderProfiles();
    }, []);
    const listMyProviderProfileChanges = useCallback(async (limit = 0) => {
        return await wasmListMyProviderProfileChanges(limit);
    }, []);
    const submitProviderProfileChange = useCallback(async (args: Parameters<typeof wasmSubmitProviderProfileChange>[0]) => {
        return await wasmSubmitProviderProfileChange(args);
    }, []);
    const resubmitProviderProfileChange = useCallback(async (requestId: string, display: Record<string, any>, slug = "") => {
        return await wasmResubmitProviderProfileChange(requestId, display, slug);
    }, []);
    const adminListProviderProfileChanges = useCallback(async (status = "submitted", limit = 0, offset = 0) => {
        return await wasmAdminListProviderProfileChanges(status, limit, offset);
    }, []);
    const adminApproveProviderProfileChange = useCallback(async (requestId: string, review: Parameters<typeof wasmAdminApproveProviderProfileChange>[1] = {}) => {
        return await wasmAdminApproveProviderProfileChange(requestId, review);
    }, []);
    const adminRejectProviderProfileChange = useCallback(async (requestId: string, message: string, note = "") => {
        return await wasmAdminRejectProviderProfileChange(requestId, message, note);
    }, []);
    const reassignProviderProfileRecipient = useCallback(async (profileId: string, identityId: string) => {
        return await wasmReassignProviderProfileRecipient(profileId, identityId);
    }, []);
    // Demand brokerage console (docs/Demand-Brokerage-Architecture.md). Pass-
    // throughs: filtering, the allow-list, and every policy refusal live in the
    // shared Go device library, so this layer adds no judgement of its own. Errors
    // are NOT swallowed — an admin surface must show a real failure rather than
    // render an empty board that reads as "no demand".
    const adminListOpenDemand = useCallback(async (filter: Parameters<typeof wasmAdminListOpenDemand>[0] = {}) => {
        return await wasmAdminListOpenDemand(filter);
    }, []);
    const adminGetDemandDetail = useCallback(async (searchId: string) => {
        return await wasmAdminGetDemandDetail(searchId);
    }, []);
    // The onboarding follow-up queue. Pass-throughs for the same reason as the
    // demand console above: the admin gate and every refusal live server-side,
    // so this layer adds no judgement, and an error is surfaced rather than
    // swallowed — an empty queue and a failed read must not look alike.
    const adminListOnboardingRequests = useCallback(async (limit = 0) => {
        return await wasmAdminListOnboardingRequests(limit);
    }, []);
    const adminGetOnboardingRequest = useCallback(async (id: string) => {
        return await wasmAdminGetOnboardingRequest(id);
    }, []);
    const adminMarkOnboardingFollowedUp = useCallback(async (id: string) => {
        return await wasmAdminMarkOnboardingFollowedUp(id);
    }, []);
    // Recent accounts: a pass-through like the queue above. The staff-admin
    // gate lives server-side, and a refusal must surface, not read as empty.
    const adminListRecentAccounts = useCallback(async (limit = 0) => {
        return await wasmAdminListRecentAccounts(limit);
    }, []);
    // Web Push: pass-throughs, like the rest. Scoping and every refusal live
    // server-side; this layer adds no judgement and swallows no error.
    const registerPushToken = useCallback(async (sub: PushSubscriptionArgs) => {
        await wasmRegisterPushToken(sub);
    }, []);
    const revokePushToken = useCallback(async (token: string) => {
        await wasmRevokePushToken(token);
    }, []);
    const listMyPushTokens = useCallback(async () => {
        return await wasmListMyPushTokens();
    }, []);
    const pushPublicKey = useCallback(async () => {
        return await wasmPushPublicKey();
    }, []);
    const adminDemandMatches = useCallback(async (searchId: string) => {
        return await wasmAdminDemandMatches(searchId);
    }, []);
    const setProviderProfileListed = useCallback(async (profileId: string, listed: boolean) => {
        return await wasmSetProviderProfileListed(profileId, listed);
    }, []);
    const adminMintDemandOutreachToken = useCallback(async (searchId: string, label: string, ttlHours: number, profileId = "", recipientIdentityId = "") => {
        return await wasmAdminMintDemandOutreachToken(searchId, label, ttlHours, profileId, recipientIdentityId);
    }, []);
    // The review contract. The console sends CHOICES, never content — see
    // wasm/ops.ts — so nothing typed in this layer can reach the directory.
    const adminListInterestSubmissions = useCallback(async (filter: Parameters<typeof wasmAdminListInterestSubmissions>[0] = {}) => {
        return await wasmAdminListInterestSubmissions(filter);
    }, []);
    const adminApproveInterestSubmission = useCallback(async (args: Parameters<typeof wasmAdminApproveInterestSubmission>[0]) => {
        return await wasmAdminApproveInterestSubmission(args);
    }, []);
    const adminRejectInterestSubmission = useCallback(async (interestId: string, reason: string, message: string) => {
        await wasmAdminRejectInterestSubmission(interestId, reason, message);
    }, []);
    const adminAskStillLooking = useCallback(async (searchId: string) => {
        return await wasmAdminAskStillLooking(searchId);
    }, []);
    const recommendProvidersToSearch = useCallback(async (searchId: string, profileIds: string[], origin = "") => {
        return await wasmRecommendProvidersToSearch(searchId, profileIds, origin);
    }, []);
    // The PRACTICE's own board, and the two things it can do from there.
    const demandItemsForIdentity = useCallback(async (identityId = "") => {
        return await wasmDemandItemsForIdentity(identityId);
    }, []);
    const respondToDemandInvitation = useCallback(async (tokenId: string, accept: boolean, note = "") => {
        return await wasmRespondToDemandInvitation(tokenId, accept, note);
    }, []);
    const resubmitDemandInterest = useCallback(async (interestId: string, payload?: Record<string, any>, draftDisplay?: Record<string, any>) => {
        return await wasmResubmitDemandInterest(interestId, payload, draftDisplay);
    }, []);
    // The tokened outreach page, once its visitor has an account. The ANONYMOUS
    // read is not here at all — /interest fetches it with a plain POST and loads no
    // wasm, which is what keeps an acquisition page free of a device layer a
    // stranger has no use for.
    const openDemandOutreachLinkAsSelf = useCallback(async (tokenId: string, secret: string) => {
        return await wasmOpenDemandOutreachLinkAsSelf(tokenId, secret);
    }, []);
    const submitDemandInterest = useCallback(async (tokenId: string, secret: string, payload: Record<string, any>, draftDisplay?: Record<string, any>) => {
        return await wasmSubmitDemandInterest(tokenId, secret, payload, draftDisplay);
    }, []);
    // The patient-side stale check-in: render the question, then record the answer.
    const drainAvaStillLookingCheck = useCallback(async (searchId: string, nodeId: string, selfGrant: Record<string, any>, stateSnapshot: Record<string, any>, seq: number) => {
        return await wasmDrainAvaStillLookingCheck(searchId, nodeId, selfGrant, stateSnapshot, seq);
    }, []);
    const settleStillLookingCheck = useCallback(async (searchId: string, keepLooking: boolean) => {
        return await wasmSettleStillLookingCheck(searchId, keepLooking);
    }, []);
    // Staff↔patient demand messaging (§8): the thread, both sends, the patient
    // drain, staff read-state, and the console's Inbox/staff-team ops.
    const getDemandThread = useCallback(async (searchId: string) => {
        return await wasmGetDemandThread(searchId);
    }, []);
    const sendDemandMessage = useCallback(async (args: { searchId: string; recipientIdentityId: string; recipientEncKid?: string; selfIdentityId: string; text: string }) => {
        return await wasmSendDemandMessage(args);
    }, []);
    const drainAvaStaffMessages = useCallback(async (searchId: string, nodeId: string, selfGrant: Record<string, any>, stateSnapshot: Record<string, any>, seq: number) => {
        return await wasmDrainAvaStaffMessages(searchId, nodeId, selfGrant, stateSnapshot, seq);
    }, []);
    // One ordered pass over everything queued: the order and the suppression
    // rule between items are sealed into the transcript, so the library owns them.
    const drainAvaPending = useCallback(async (searchId: string, nodeId: string, selfGrant: Record<string, any>, stateSnapshot: Record<string, any>, seq: number) => {
        return await wasmDrainAvaPending(searchId, nodeId, selfGrant, stateSnapshot, seq);
    }, []);

    // The patient's own words for an answer, derived by the device library —
    // the same implementation that seals them for replay.
    const userBubbleForAnswer = useCallback(async (answerJSON: string) => {
        return await wasmUserBubbleForAnswer(answerJSON);
    }, []);
    // The share URL from the device library (§10.3): pure, no device needed.
    const shareLinkURL = useCallback(async (slug: string, profileId: string) => {
        return await wasmShareLinkURL(slug, profileId);
    }, []);

    const sendPatientStaffMessage = useCallback(async (searchId: string, nodeId: string, text: string, selfGrant: Record<string, any>, stateSnapshot: Record<string, any>, seq: number) => {
        return await wasmSendPatientStaffMessage(searchId, nodeId, text, selfGrant, stateSnapshot, seq);
    }, []);
    const setDemandMessagesRead = useCallback(async (searchId: string, read: boolean) => {
        return await wasmSetDemandMessagesRead(searchId, read);
    }, []);
    const adminListUnreadDemandThreads = useCallback(async () => {
        return await wasmAdminListUnreadDemandThreads();
    }, []);
    const adminGetStaffTeam = useCallback(async () => {
        return await wasmAdminGetStaffTeam();
    }, []);
    const adminDesignateStaffTeam = useCallback(async (teamId: string) => {
        return await wasmAdminDesignateStaffTeam(teamId);
    }, []);
    const adminCreatePayer = useCallback(async (payer: Record<string, any>, fromResearch?: import("../../wasm/ops").PayerFromResearch) => {
        return await wasmAdminCreatePayer(payer, fromResearch);
    }, []);
    const adminUpdatePayer = useCallback(async (payer: Record<string, any>, fromResearch?: import("../../wasm/ops").PayerFromResearch) => {
        return await wasmAdminUpdatePayer(payer, fromResearch);
    }, []);
    const adminRetirePayer = useCallback(async (payerId: string, into: string) => {
        return await wasmAdminRetirePayer(payerId, into);
    }, []);
    const adminSetAvaPayerChips = useCallback(async (state: string, chips: string[]) => {
        return await wasmAdminSetAvaPayerChips(state, chips);
    }, []);
    const adminImportPayers = useCallback(async (master: Record<string, any>, overwrite: boolean) => {
        return await wasmAdminImportPayers(master, overwrite);
    }, []);
    const adminPreviewPayerChange = useCallback(async (change: Record<string, any>) => {
        return await wasmAdminPreviewPayerChange(change);
    }, []);
    const adminPayerUsedBy = useCallback(async (payerId: string) => {
        return await wasmAdminPayerUsedBy(payerId);
    }, []);
    const adminPayersByState = useCallback(async () => {
        return await wasmAdminPayersByState();
    }, []);
    const adminPayerChangeLog = useCallback(async (payerId: string, limit = 0) => {
        return await wasmAdminPayerChangeLog(payerId, limit);
    }, []);
    const adminPayerResearchSources = useCallback(async (states: string[]) => {
        return await wasmAdminPayerResearchSources(states);
    }, []);
    const adminResearchPayer = useCallback(async (name: string, states: string[]) => {
        return await wasmAdminResearchPayer(name, states);
    }, []);
    const adminRecheckPayer = useCallback(async (payerId: string) => {
        return await wasmAdminRecheckPayer(payerId);
    }, []);
    const adminResearchPayerAgain = useCallback(async (runId: string) => {
        return await wasmAdminResearchPayerAgain(runId);
    }, []);
    const adminPayerResearchRuns = useCallback(async (status = '', before = '', limit = 0) => {
        return await wasmAdminPayerResearchRuns(status, before, limit);
    }, []);
    const adminPayerResearchRun = useCallback(async (runId: string) => {
        return await wasmAdminPayerResearchRun(runId);
    }, []);
    const adminDecidePayerFinding = useCallback(async (runId: string, ordinal: number, decision: 'accepted' | 'rejected' | '') => {
        return await wasmAdminDecidePayerFinding(runId, ordinal, decision);
    }, []);
    const adminLeaveOutPayerResearch = useCallback(async (runId: string, reason: string) => {
        return await wasmAdminLeaveOutPayerResearch(runId, reason);
    }, []);
    const adminRevokeDemandOutreachToken = useCallback(async (tokenId: string) => {
        await wasmAdminRevokeDemandOutreachToken(tokenId);
    }, []);
    const getProviderProfile = useCallback(async (slug: string) => {
        try { return await wasmGetProviderProfile(slug); }
        catch (e) { console.warn('[getProviderProfile]', e); return null; }
    }, []);
    // Practice profiles (§5.17.1).
    const resolveBookLink = useCallback(async (slug: string) => {
        try { return await wasmResolveBookLink(slug); }
        catch (e) { console.warn('[resolveBookLink]', e); return { kind: 'none' as const }; }
    }, []);
    const listPracticeProfiles = useCallback(async () => {
        return await wasmListPracticeProfiles();
    }, []);
    const createPracticeProfile = useCallback(async (slug: string, display: Record<string, any>) => {
        return await wasmCreatePracticeProfile(slug, display);
    }, []);
    const updatePracticeProfile = useCallback(async (practiceId: string, slug: string, display: Record<string, any>) => {
        return await wasmUpdatePracticeProfile(practiceId, slug, display);
    }, []);
    const deletePracticeProfile = useCallback(async (practiceId: string) => {
        return await wasmDeletePracticeProfile(practiceId);
    }, []);
    const setProviderProfilePractice = useCallback(async (profileId: string, practiceId: string) => {
        return await wasmSetProviderProfilePractice(profileId, practiceId);
    }, []);
    const arrangePracticeMembers = useCallback(async (practiceId: string, profileIds: string[]) => {
        return await wasmArrangePracticeMembers(practiceId, profileIds);
    }, []);
    const getReferenceData = useCallback(async () => {
        return await wasmGetReferenceData();
    }, []);
    // Registry SUGGESTIONS for an NPI. Read-only; the editor offers them and a
    // human decides. A failure is reported, not thrown, so a registry outage
    // can never block editing a profile.
    const nppesLookup = useCallback(async (npi: string, displayName: string) => {
        try {
            return await wasmNppesLookup(npi, displayName);
        } catch (e) {
            return {
                success: false,
                error: e instanceof Error ? e.message : String(e),
                found: false,
                taxonomies: [],
            };
        }
    }, []);
    // The public pages of an unpublished profile, for staff. A failure is
    // reported, not thrown: a preview that cannot render must never take the
    // editor down with it.
    const adminRenderProviderPreview = useCallback(async (profileId: string, display: Record<string, any>, page: string) => {
        try {
            return await wasmAdminRenderProviderPreview(profileId, display, page);
        } catch (e) {
            return {
                success: false,
                error: e instanceof Error ? e.message : String(e),
                pages: [],
            };
        }
    }, []);
    // Preview links: the envelope comes back as-is (a refusal is for a human);
    // list and revoke throw on failure like their outreach counterparts.
    const adminMintProviderPreviewLink = useCallback(async (profileId: string, label: string, ttlDays = 0) => {
        try {
            return await wasmAdminMintProviderPreviewLink(profileId, label, ttlDays);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);
    const adminRevokeProviderPreviewLink = useCallback(async (tokenId: string) => {
        await wasmAdminRevokeProviderPreviewLink(tokenId);
    }, []);
    const adminListProviderPreviewLinks = useCallback(async (profileId: string) => {
        return await wasmAdminListProviderPreviewLinks(profileId);
    }, []);
    const createProviderProfile = useCallback(async (identityId: string, slug: string, display: Record<string, any>) => {
        return await wasmCreateProviderProfile(identityId, slug, display);
    }, []);
    const updateProviderProfile = useCallback(async (profileId: string, identityId: string, slug: string, display: Record<string, any>) => {
        return await wasmUpdateProviderProfile(profileId, identityId, slug, display);
    }, []);
    const deleteProviderProfile = useCallback(async (profileId: string) => {
        return await wasmDeleteProviderProfile(profileId);
    }, []);
    const uploadProviderAsset = useCallback(async (contentBase64: string, contentType: string) => {
        return await wasmUploadProviderAsset(contentBase64, contentType);
    }, []);
    const startAvaSearch = useCallback(async (
        targetProfileSlug?: string,
        utm?: { utmSource?: string; utmMedium?: string; utmCampaign?: string; utmContent?: string },
    ) => {
        return await wasmStartAvaSearch(targetProfileSlug, utm);
    }, []);
    const previewAvaSearch = useCallback(async (entry?: string) => {
        return await wasmPreviewAvaSearch(entry);
    }, []);
    // Pass-through: the projection, its allow-list and the §5.10 guardian-consent
    // derivation all live in the shared library, so this layer adds none.
    const buildAppointmentRequestDetails = useCallback(async (state: Record<string, any>) => {
        return await wasmBuildAppointmentRequestDetails(state);
    }, []);
    const advanceAvaTurn = useCallback(async (searchId: string, nodeId: string, answerJSON: string, selfGrant: Record<string, any>, stateSnapshot: Record<string, any>, seq: number, userBubble = "", stateOverrides?: Record<string, any>) => {
        // clientEvents undefined = "whatever this build can drive", answered by
        // the shared library beside the code that drives it. This used to mean
        // "declares nothing": the ops-layer default never reached here, so the
        // real app path silently took the §4.11 legacy route on every advance
        // while the constant that was supposed to prevent that sat unused.
        return await wasmAdvanceAvaTurn(searchId, nodeId, answerJSON, selfGrant, stateSnapshot, seq, userBubble, undefined, stateOverrides);
    }, []);
    // The §4.8 send obligation, discharged whole: fan-out then advance, ordered
    // by the shared library (device.SendInquiriesAndAdvance) — never here.
    const sendInquiriesAndAdvance = useCallback(async (searchId: string, nodeId: string, providers: Array<Record<string, any>>, revising: boolean, selfGrant: Record<string, any>, stateSnapshot: Record<string, any>, seq: number, stateOverrides?: Record<string, any>) => {
        return await wasmSendInquiriesAndAdvance(searchId, nodeId, providers, revising, selfGrant, stateSnapshot, seq, stateOverrides);
    }, []);
    // Pass-through: every judgement (what was queued, whether the state accepts it,
    // when to ack) lives in the shared device library, so this layer adds none.
    const drainAvaRecommendations = useCallback(async (searchId: string, nodeId: string, selfGrant: Record<string, any>, stateSnapshot: Record<string, any>, seq: number) => {
        return await wasmDrainAvaRecommendations(searchId, nodeId, selfGrant, stateSnapshot, seq);
    }, []);
    const runAvaMatch = useCallback(async (searchId: string, nodeId: string, selfGrant: Record<string, any>, stateSnapshot: Record<string, any>, seq: number) => {
        return await wasmRunAvaMatch(searchId, nodeId, selfGrant, stateSnapshot, seq);
    }, []);
    const suggestAvaProviders = useCallback(async (searchId: string, selfGrant: Record<string, any>, stateSnapshot: Record<string, any>, excludeProfileIds: string[] = []) => {
        return await wasmSuggestAvaProviders(searchId, selfGrant, stateSnapshot, excludeProfileIds);
    }, []);
    const bindAvaSearchToAccount = useCallback(async (searchId: string, selfGrant: Record<string, any>, stateSnapshot: Record<string, any>) => {
        return await wasmBindAvaSearchToAccount(searchId, selfGrant, stateSnapshot);
    }, []);
    const sendAppointmentRequestInquiries = useCallback(async (searchId: string, inquiries: WasmAppointmentInquiryInput[]) => {
        return await wasmSendAppointmentRequestInquiries(searchId, inquiries);
    }, []);
    const listAppointmentRequests = useCallback(async (args: WasmListAppointmentRequestsArgs) => {
        return await wasmListAppointmentRequests(args);
    }, []);
    const makeAppointmentOffer = useCallback(async (args: WasmMakeOfferInput) => {
        return await wasmMakeAppointmentOffer(args);
    }, []);
    const acceptAppointmentOffer = useCallback(async (offerId: string) => {
        return await wasmAcceptAppointmentOffer(offerId);
    }, []);
    const recordAppointmentOutcome = useCallback(async (requestId: string, outcome: string) => {
        return await wasmRecordAppointmentOutcome(requestId, outcome);
    }, []);
    const rateProvider = useCallback(async (requestId: string, dimensions: Record<string, number>) => {
        return await wasmRateProvider(requestId, dimensions);
    }, []);
    const ratePatient = useCallback(async (requestId: string, score: number, conductTags: string[]) => {
        return await wasmRatePatient(requestId, score, conductTags);
    }, []);
    const getMyPatientReputation = useCallback(async () => {
        return await wasmGetMyPatientReputation();
    }, []);
    const ratingsEnabled = useCallback(async () => {
        return await wasmRatingsEnabled();
    }, []);
    const declineAppointmentRequest = useCallback(async (requestId: string) => {
        return await wasmDeclineAppointmentRequest(requestId);
    }, []);
    const setAppointmentMessagesRead = useCallback(async (requestId: string, read: boolean) => {
        return await wasmSetAppointmentMessagesRead(requestId, read);
    }, []);
    const requestAppointmentForms = useCallback(async (requestId: string, templateIds: string[]) => {
        return await wasmRequestAppointmentForms(requestId, templateIds);
    }, []);
    const recordAppointmentFormSubmission = useCallback(async (requestId: string, templateId: string, formSendId: string) => {
        return await wasmRecordAppointmentFormSubmission(requestId, templateId, formSendId);
    }, []);
    const declineAppointmentOffer = useCallback(async (offerId: string) => {
        return await wasmDeclineAppointmentOffer(offerId);
    }, []);
    const withdrawAppointmentOffer = useCallback(async (offerId: string) => {
        return await wasmWithdrawAppointmentOffer(offerId);
    }, []);
    const sendAppointmentMessage = useCallback(async (args: { requestId: string; recipientIdentityId: string; selfIdentityId: string; text: string }) => {
        return await wasmSendAppointmentMessage(args);
    }, []);
    const deleteAppointmentSearch = useCallback(async (searchId: string) => {
        return await wasmDeleteAppointmentSearch(searchId);
    }, []);
    const archiveAppointmentSearch = useCallback(async (searchId: string) => {
        return await wasmArchiveAppointmentSearch(searchId);
    }, []);
    const unarchiveAppointmentSearch = useCallback(async (searchId: string) => {
        return await wasmUnarchiveAppointmentSearch(searchId);
    }, []);
    const withdrawAppointmentSearch = useCallback(async (searchId: string, recipientId = "", reason = "") => {
        return await wasmWithdrawAppointmentSearch(searchId, recipientId, reason);
    }, []);
    const getAppointmentThread = useCallback(async (requestId: string) => {
        return await wasmGetAppointmentThread(requestId);
    }, []);
    const hasAppointmentRecipientAccess = useCallback(async () => {
        return await wasmHasAppointmentRecipientAccess();
    }, []);
    const hasTeamMembership = useCallback(async () => {
        return await wasmHasTeamMembership();
    }, []);
    const listMyAppointmentSearches = useCallback(async () => {
        // Propagate failures so the rail can tell a device/transport error (→ show
        // a retry) from a genuinely empty list. Swallowing to [] made a transient
        // "unknown device" blip look like the patient's searches had vanished.
        return await wasmListMyAppointmentSearches();
    }, []);
    // PROPAGATE (mirrors listMyAppointmentSearches): swallowing to [] made a
    // transient auth/'unknown device' error look like the patient asked nobody.
    // Consumers handle the throw — SearchDetail shows "Could not load this search",
    // the /ava deep-link resolver treats it as inconclusive (no poison), and the
    // revise/withdraw paths have their own catch. (#16)
    const listMyRequestInquiries = useCallback(async (searchId: string) => {
        return await wasmListMyRequestInquiries(searchId);
    }, []);
    const decryptSearchState = useCallback(async (selfGrant: Record<string, any>, stateSnapshot: Record<string, any>) => {
        return await wasmDecryptSearchState(selfGrant, stateSnapshot);
    }, []);
    const getAvaTranscript = useCallback(async (searchId: string) => {
        return await wasmGetAvaTranscript(searchId);
    }, []);
    const getSavedForm = useCallback(async (formId: string) => {
        try { return await wasmGetSavedForm(formId); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);

    // --- Payment vendors + billing (all pass-through) ----------------
    const listPaymentVendors = useCallback(async (identityId: string) => {
        try { return await wasmListPaymentVendors(identityId); }
        catch (e) { console.warn('[listPaymentVendors]', e); return []; }
    }, []);
    const getPaymentVendor = useCallback(async (opts: { integrationId?: string; identityId?: string; vendorType?: string }) => {
        try { return await wasmGetPaymentVendor(opts); }
        catch (e) { console.warn('[getPaymentVendor]', e); return null; }
    }, []);
    const deletePaymentVendor = useCallback(async (identityId: string, opts: { integrationId?: string; vendorType?: string }) => {
        try { return await wasmDeletePaymentVendor(identityId, opts); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);
    const validatePaymentVendor = useCallback(async (identityId: string, integrationId: string) => {
        try { return await wasmValidatePaymentVendor(identityId, integrationId); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);
    const createSquareOAuthUrl = useCallback(async (identityId: string, integrationId?: string) => {
        try { return await wasmCreateSquareOAuthUrl(identityId, integrationId); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);
    const completeSquareOAuth = useCallback(async (code: string, state: string) => {
        try { return await wasmCompleteSquareOAuth(code, state); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);
    const listPlans = useCallback(async (includeInactive = false) => {
        try { return await wasmListPlans(includeInactive); }
        catch (e) { console.warn('[listPlans]', e); return []; }
    }, []);
    const getMyLicenses = useCallback(async (activeOnly = false) => {
        try { return await wasmGetMyLicenses(activeOnly); }
        catch (e) { console.warn('[getMyLicenses]', e); return []; }
    }, []);
    const getMyUsage = useCallback(async (subscriptionId: string) => {
        return await wasmGetMyUsage(subscriptionId);
    }, []);
    const subscribeMe = useCallback(async (args: { planId: string; billingInterval: string; paymentMethodId?: string }) => {
        return await wasmSubscribeMe(args);
    }, []);
    const changeSubscriptionPlan = useCallback(async (subscriptionId: string, newPlanId: string, billingInterval?: string) => {
        return await wasmChangeSubscriptionPlan(subscriptionId, newPlanId, billingInterval);
    }, []);
    const cancelSubscription = useCallback(async (subscriptionId: string, immediate = false) => {
        return await wasmCancelSubscription(subscriptionId, immediate);
    }, []);
    const reactivateSubscription = useCallback(async (subscriptionId: string) => {
        return await wasmReactivateSubscription(subscriptionId);
    }, []);

    // --- Form templates (all pass-through to shared library) --------
    const createFormTemplate = useCallback(async (
        template: Record<string, any>,
        opts?: { createForIdentityId?: string; selectedSubscriptionId?: string },
    ) => {
        try {
            return await wasmCreateFormTemplate(template, opts);
        } catch (e) {
            return {
                success: false,
                error: e instanceof Error ? e.message : String(e),
                claimed: false,
                limitExceeded: false,
                limitMessage: '',
                upgradeCTA: '',
            };
        }
    }, []);
    const updateFormTemplate = useCallback(async (template: Record<string, any>) => {
        try { return await wasmUpdateFormTemplate(template); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);
    const updateFormTemplateName = useCallback(async (id: string, name: string) => {
        try { return await wasmUpdateFormTemplateName(id, name); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);
    const updateFormTemplateMarks = useCallback(async (
        id: string,
        marks: Array<Record<string, any>>,
        presubmitConditions?: Array<Record<string, any>>,
    ) => {
        try { return await wasmUpdateFormTemplateMarks(id, marks, presubmitConditions); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);
    const updateFormTemplatePDF = useCallback(async (id: string, pdf: { sha256?: string; bytes?: string }) => {
        try { return await wasmUpdateFormTemplatePDF(id, pdf); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);
    const updateFormTemplateRecipient = useCallback(async (id: string, recipientUserId: string) => {
        try { return await wasmUpdateFormTemplateRecipient(id, recipientUserId); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);
    const updateFormTemplateAllowAnonymous = useCallback(async (id: string, allow: boolean) => {
        try { return await wasmUpdateFormTemplateAllowAnonymous(id, allow); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);
    const bindAnonymousFormSubmission = useCallback(async (sendId: string) => {
        try { return await wasmBindAnonymousFormSubmission(sendId); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);
    const deleteFormTemplate = useCallback(async (id: string, opts?: { force?: boolean }) => {
        try { return await wasmDeleteFormTemplate(id, opts); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);
    const claimFormTemplate = useCallback(async (args: {
        id: string;
        claimForIdentityId?: string;
        allowTransfer?: boolean;
        selectedSubscriptionId?: string;
    }) => {
        try { return await wasmClaimFormTemplate(args); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);
    const listFormTemplates = useCallback(async (args?: any) => {
        try { return await wasmListFormTemplates(args); }
        catch (e) {
            return { items: [], nextCursor: '', totalCount: 0, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);
    const readFormTemplatePdf = useCallback(async (opts: { sha256?: string; templateId?: string }) => {
        try { return await wasmReadFormTemplatePdf(opts); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);
    const assignPaymentIntegrationToTemplate = useCallback(async (templateId: string, paymentIntegrationId?: string | null) => {
        try { return await wasmAssignPaymentIntegrationToTemplate(templateId, paymentIntegrationId); }
        catch (e) { return { success: false, error: e instanceof Error ? e.message : String(e) }; }
    }, []);

    // Create team. Routes through the shared device library via
    // WASM — see operations_teams.go:CreateTeam.
    const createTeam = useCallback(async (team: Team): Promise<CreateTeamResult> => {
        try {
            const r = await wasmCreateTeam(team.name || '');
            if (r.success) invalidateLocalCacheForTeams();
            return { success: r.success, error: r.error, teamId: r.teamId };
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Pairing (new-device side): poll the server for approval. Uses
    // the persisted browser-device seed (via prepareDeviceArgs),
    // matching the sigKid registered by provisionWithPairing.
    const checkPairingStatus = useCallback(async (pairingId: string) => {
        try {
            return await wasmCheckPairingStatus(pairingId);
        } catch (e) {
            return {
                status: '',
                isCompleted: false,
                expiresIn: 0,
                error: e instanceof Error ? e.message : String(e),
            };
        }
    }, []);

    // Pairing (approver side): look up, distribute keys, provision,
    // and complete. Runs on the authenticated device.
    const approvePairing = useCallback(async (pairingCode: string, deviceName?: string) => {
        try {
            return await wasmApprovePairing(pairingCode, deviceName);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // List all devices registered under the authenticated user.
    const listDevices = useCallback(async (): Promise<RemoteDeviceInfo[]> => {
        try {
            return await wasmListDevices();
        } catch (e) {
            console.warn('[listDevices] error:', e);
            return [];
        }
    }, []);

    // Revoke one or more devices. Triggers a full PUK rotation as
    // part of the ceremony; routes through the shared device library.
    const revokeDevices = useCallback(async (
        sigKids: string[],
        onProgress?: ProgressCallback,
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            return await wasmRevokeDevices(sigKids, onProgress);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Push this device's PxK material to all other devices. Edge-case
    // fix when distributions for a peer device are missing server-side
    // but this device can re-encrypt them (mirror of the CLI's
    // `practicemd keyring push`).
    const pushKeys = useCallback(async (
        onProgress?: ProgressCallback,
    ): Promise<{ success: boolean; error?: string; distributed?: number }> => {
        try {
            return await wasmPushKeys(onProgress);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Phase 1 of account creation. Routes through the shared device
    // library via WASM — see operations.go:CreateAccount.
    const createUser = useCallback(async (user: UserInfo, deviceName?: string): Promise<PendingAccount> => {
        try {
            return await wasmCreateUser(user.id, user.email || '', deviceName);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // One-shot prepare+register. Use ONLY from programmatic/test
    // contexts where blocking on "save your mnemonic first" isn't
    // possible — real UX flows (authorize page, settings) must call
    // preparePaperKey + registerPaperKey separately.
    const generatePaperKey = useCallback(async (paperKeyName?: string): Promise<ProvisionDeviceResult> => {
        try {
            return await wasmGeneratePaperKey(paperKeyName);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Prepare a paper key locally (no server contact). The user
    // should be shown the mnemonic and confirm they've saved it
    // offline; only then call registerPaperKey with the returned
    // PreparedPaperKey.
    const preparePaperKey = useCallback(async (): Promise<PreparedPaperKey> => {
        return await wasmPreparePaperKey();
    }, []);

    // Register a previously-prepared paper key with the server.
    const registerPaperKey = useCallback(async (
        prepared: PreparedPaperKey,
        paperKeyName?: string,
    ): Promise<ProvisionDeviceResult> => {
        try {
            return await wasmRegisterPaperKey(prepared, paperKeyName);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Update user profile info (name, email, etc.). Routes through the
    // shared device library via WASM — see
    // common/device/httpbackend/methods.go:UpdateUserInfo.
    const updateUserinfo = useCallback(async (
        updates: { givenName?: string; familyName?: string; email?: string }
    ) => {
        try {
            return await wasmUpdateUserinfo(updates);
        } catch (e) {
            return {
                success: false,
                error: e instanceof Error ? e.message : String(e),
            };
        }
    }, []);

    // Phase 2 of account creation. Takes the PendingAccount returned
    // by phase 1 (contains userId + pukSeedHex + deviceSeedHex +
    // email) plus the 6-digit code the user entered. Routes through
    // the shared device library via WASM — see
    // operations.go:AccountCreation.CompleteWithCode. Returns the
    // device seed and populated keyring so the caller can persist
    // them (promote the freshly-minted device into IndexedDB).
    const completeUserCreation = useCallback(async (
        pending: PendingAccount,
        code: string,
    ): Promise<CompleteUserCreationResult> => {
        try {
            return await wasmCompleteUserCreation(pending, code);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // ENG-81 attestation — see useDevice.types.ts.
    const provePaperKey = useCallback(async (
        words: string,
    ): Promise<{ success: boolean; provenAt?: string; error?: string }> => {
        try {
            return await wasmProvePaperKeyWithWords(words);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // ENG-81 pre-ceremony gate — see useDevice.types.ts.
    const verifyCreationCode = useCallback(async (
        pending: PendingAccount,
        code: string,
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            return await wasmVerifyCreationCode(pending, code);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // ENG-81 atomic commit — replaces completeUserCreation + the separate
    // registerPaperKey for NEW accounts. See useDevice.types.ts.
    const finalizeUserCreation = useCallback(async (
        pending: PendingAccount,
        code: string,
        recovery: RecoveryGroupEntry[],
    ): Promise<CompleteUserCreationResult> => {
        try {
            return await wasmFinalizeUserCreation(pending, code, recovery);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Release 1: passkey recovery devices. Thin pass-throughs; the crypto
    // (HKDF, envelopes) is the wasm's.
    const prepareRecoveryDevice = useCallback(async (): Promise<PreparedRecoveryDevice> => {
        return await wasmPrepareRecoveryDevice();
    }, []);
    const wrapRecoverySeedForPasskey = useCallback(async (seedHex: string, prfOutputHex: string): Promise<string> => {
        return await wasmWrapRecoverySeedForPasskey(seedHex, prfOutputHex);
    }, []);
    const unwrapRecoverySeedWithPasskey = useCallback(async (envelopeB64: string, prfOutputHex: string): Promise<string> => {
        return await wasmUnwrapRecoverySeedWithPasskey(envelopeB64, prfOutputHex);
    }, []);
    const registerPasskeyDevice = useCallback(async (args: {
        prepared: PreparedRecoveryDevice; name?: string; credentialId: string; publicKey: string; envelope: string;
    }): Promise<{ success: boolean; error?: string }> => {
        try {
            return await wasmRegisterPasskeyDevice(args);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);
    const recoveryEnvelope = useCallback(async (assertion: {
        credentialId: string; clientDataJSON: string; authenticatorData: string; signature: string;
    }): Promise<{ found: boolean; envelope?: string }> => {
        return await wasmRecoveryEnvelope(assertion);
    }, []);

    // Add user to team. userIdOrEmail may be a user id, email, or
    // kid; the server resolves it.
    const addUserToTeam = useCallback(async (
        teamId: string,
        userIdOrEmail: string,
        permissions: TeamPermission[],
    ): Promise<AddUserToTeamResult> => {
        try {
            return await wasmAddUserToTeam(teamId, userIdOrEmail, permissions as any);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Invite an existing user to a team via consent (an in-band message).
    const inviteExistingUserToTeam = useCallback(async (
        teamId: string,
        inviteeIdOrEmail: string,
        permissions: TeamPermission[],
    ) => {
        try {
            return await wasmInviteExistingUserToTeam(teamId, inviteeIdOrEmail, permissions as any);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Accept a team invitation by IBM item id (also materializes the team key).
    const acceptTeamInvitation = useCallback(async (itemId: string) => {
        try {
            return await wasmAcceptTeamInvitation(itemId);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Decline a team invitation by IBM item id.
    const declineTeamInvitation = useCallback(async (itemId: string) => {
        try {
            return await wasmDeclineTeamInvitation(itemId);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Admin: withdraw a still-pending invitation by its pending_membership id.
    const withdrawTeamInvitation = useCallback(async (pendingId: string) => {
        try {
            return await wasmWithdrawTeamInvitation(pendingId);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Request to join a team (the request direction).
    const requestToJoinTeam = useCallback(async (teamId: string, message: string) => {
        try {
            return await wasmRequestToJoinTeam(teamId, message);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Admin: approve a pending join request (wraps the PTK to the requester).
    const approveJoinRequest = useCallback(async (
        pendingId: string,
        teamId: string,
        requesterUserId: string,
    ) => {
        try {
            return await wasmApproveJoinRequest(pendingId, teamId, requesterUserId);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Admin: deny a pending join request by its pending_membership id.
    const denyJoinRequest = useCallback(async (pendingId: string) => {
        try {
            return await wasmDenyJoinRequest(pendingId);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Admin view: a team's pending (unapproved) join requests.
    const listTeamJoinRequests = useCallback(async (teamId: string) => {
        try {
            return await wasmListTeamJoinRequests(teamId);
        } catch (e) {
            console.warn('[listTeamJoinRequests] error:', e);
            return [];
        }
    }, []);

    // Invite an email with no account (the non-user path). siteUrl builds the link.
    const inviteEmailToTeam = useCallback(async (
        teamId: string,
        inviteeEmail: string,
        siteUrl: string,
        permissions: TeamPermission[],
    ) => {
        try {
            return await wasmInviteEmailToTeam(teamId, inviteeEmail, siteUrl, permissions as any);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Claim a non-user invitation from its link (also materializes the team key).
    const claimEmailInvitation = useCallback(async (pendingId: string, fragment: string) => {
        try {
            return await wasmClaimEmailInvitation(pendingId, fragment);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // List this device's live in-band messages (the inbox).
    const listPendingIbms = useCallback(async () => {
        try {
            return await wasmListPendingIbms();
        } catch (e) {
            console.warn('[listPendingIbms] error:', e);
            return [];
        }
    }, []);

    // The user's pending notifications for the center (user + team recipient).
    // PROPAGATE failures (mirrors listMyAppointmentSearches): swallowing to []
    // made an expired/unknown-device auth error look like the user's
    // notifications had vanished — the center must distinguish a real error
    // (show retry / re-unlock) from genuinely having nothing pending.
    const notificationCenterItems = useCallback(async () => {
        return await wasmNotificationCenterItems();
    }, []);

    // The per-user NEEDS YOU unread badge count. PROPAGATE failures rather than
    // returning 0 — a swallowed auth error masquerading as a zero badge hides
    // every pending action item.
    const notificationUnreadCount = useCallback(async () => {
        return await wasmNotificationUnreadCount();
    }, []);

    // Ack-on-open: record this device's ack+dismiss on a notification. Returns
    // true on success so the caller only applies the optimistic "read" move when
    // the server actually recorded it (the server may reject — e.g. a non-admin
    // dismissing a role-gated team-join-request — in which case we must NOT fold).
    const dismissStateItem = useCallback(async (itemId: string): Promise<boolean> => {
        try {
            await wasmDismissStateItem(itemId);
            return true;
        } catch (e) {
            console.warn('[dismissStateItem] error:', e);
            return false;
        }
    }, []);

    // Admin view: a team's pending (unaccepted) invitations.
    const listTeamPendingInvitations = useCallback(async (teamId: string) => {
        try {
            return await wasmListTeamPendingInvitations(teamId);
        } catch (e) {
            console.warn('[listTeamPendingInvitations] error:', e);
            return [];
        }
    }, []);

    // Admin auto-handler: perform any pending team-PTK rotations (the backstop).
    const handlePtkRotations = useCallback(async () => {
        try {
            return await wasmHandlePtkRotations();
        } catch (e) {
            console.warn('[handlePtkRotations] error:', e);
            return { handled: 0, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Replace a member's entire permissions list.
    const updateMemberPermissions = useCallback(async (
        teamId: string,
        userId: string,
        permissions: TeamPermission[],
    ): Promise<UpdateMemberPermissionsResult> => {
        try {
            return await wasmUpdateMemberPermissions(teamId, userId, permissions as any);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // Remove member from team with full PTK rotation. Persists the
    // caller's updated keyring automatically.
    const removeMemberFromTeam = useCallback(async (
        teamId: string,
        userIdOrEmail: string,
    ): Promise<RemoveMemberResult> => {
        try {
            return await wasmRemoveMemberFromTeam(teamId, userIdOrEmail);
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e) };
        }
    }, []);

    // List teams the authenticated user is a member of.
    const listTeams = useCallback(async () => {
        try {
            return await wasmListTeams();
        } catch (e) {
            console.warn('[listTeams] error:', e);
            return [];
        }
    }, []);

    // List members of a team.
    const listTeamMembers = useCallback(async (teamId: string) => {
        try {
            return await wasmListTeamMembers(teamId);
        } catch (e) {
            console.warn('[listTeamMembers] error:', e);
            return [];
        }
    }, []);

    // Delete team function
    const deleteTeam = useCallback(async (teamId: string): Promise<DeleteTeamResult> => {
        try {
            const r = await wasmDeleteTeam(teamId);
            if (r.success) invalidateLocalCacheForTeams();
            return r;
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : String(error),
            };
        }
    }, []);

    // Write an E2E encrypted send to a single known recipient. Thin
    // wrapper over writeSendMulti for the common case — see
    // common/device/operations_sends.go:CreateSend.
    const writeSend = useCallback(async (
        recipientId: string,
        itemType: string,
        plaintext: Uint8Array,
        metadata?: Record<string, any>,
        allowAnonymousSend?: boolean,
    ): Promise<WriteSendResult> => {
        try {
            // With no signed-in user, an explicitly-allowed anonymous send
            // routes through the anonymous op: a silently-minted local
            // device signs as itself, and the server admits the send only
            // through the anonymous form gate (opted-in claimed template,
            // rate limits). With a user present the flag is a no-op.
            if (allowAnonymousSend && !userInfo?.id) {
                const r = await wasmWriteSendAnonymous(recipientId, itemType, plaintext, metadata || null);
                return { success: r.success, sendId: r.sendId, error: r.error };
            }
            const r = await wasmWriteSend(recipientId, itemType, plaintext, metadata || null);
            return { success: r.success, sendId: r.sendId, error: r.error };
        } catch (e) {
            return {
                success: false,
                error: e instanceof Error ? e.message : String(e),
            };
        }
    }, [userInfo?.id]);

    // Write an E2E encrypted send to multiple recipients. Accepts the
    // full RecipientSpec shape (known / bearer / email_verified).
    // Routes through wasm.writeSend which marshals to Go's
    // device.CreateSend.
    const writeSendMulti = useCallback(async (
        recipients: any[],
        itemType: string,
        plaintext: Uint8Array,
        metadata?: Record<string, any>,
    ): Promise<WriteSendResult> => {
        try {
            const r = await wasmWriteSendMulti(recipients, itemType, plaintext, metadata || null);
            return {
                success: r.success,
                sendId: r.sendId,
                error: r.error,
                ephemeralFragments: r.ephemeralFragments,
            };
        } catch (e) {
            return {
                success: false,
                error: e instanceof Error ? e.message : String(e),
            };
        }
    }, []);

    // List sends filtered by recipientId (inbox) or senderId (outbox).
    // Metadata-only; no payload crypto. Matches wasmbackend.ListSends.
    const listSendsFn = useCallback(async (args: any) => {
        return await wasmListSends(args);
    }, []);

    // Look up an identity (user or team) by id, email, or kid.
    // Public query; no device signature needed. Main use is resolving
    // a recipient's encKid before writeSend.
    const getIdentityFn = useCallback(async (key: string) => {
        return await wasmGetIdentity(key);
    }, []);

    // Positional by-kid batch; public query, no signature.
    const getIdentitiesFn = useCallback(async (kids: string[]) => {
        return await wasmGetIdentities(kids);
    }, []);

    // Read and decrypt an E2E encrypted send. ephemeralFragment is the
    // base64url URL fragment for ephemeral recipients; pass undefined
    // for authenticated recipient access.
    const readSendE2E = useCallback(async (sendId: string, ephemeralFragment?: string, recoveryHintIdentityId?: string): Promise<ReadSendResult> => {
        try {
            const r = await wasmReadSend(sendId, ephemeralFragment, recoveryHintIdentityId);
            if (!r.success) {
                return { success: false, error: r.error };
            }
            return {
                success: true,
                id: r.id,
                itemType: r.itemType,
                data: r.data,
                metadata: r.metadata,
                createdAt: r.createdAt ? new Date(r.createdAt) : undefined,
            };
        } catch (e) {
            return {
                success: false,
                error: e instanceof Error ? e.message : String(e),
            };
        }
    }, []);

    // Unauthenticated bearer-send read. Designed for the zero-account
    // visitor case: someone who has only a shared URL and fragment and
    // is not signed in at all. Uses the anonymous WASM binding which
    // fires an unsigned readSendV2 — no device state required.
    const readSendAnonymous = useCallback(async (sendId: string, fragment: string): Promise<ReadSendResult> => {
        try {
            const r = await wasmReadSendAnonymous(sendId, fragment);
            if (!r.success) {
                return { success: false, error: r.error };
            }
            return {
                success: true,
                id: r.id,
                itemType: r.itemType,
                data: r.data,
                metadata: r.metadata,
                createdAt: r.createdAt ? new Date(r.createdAt) : undefined,
            };
        } catch (e) {
            return {
                success: false,
                error: e instanceof Error ? e.message : String(e),
            };
        }
    }, []);

    const updateRecipientStateFn = useCallback(
        async (
            sendId: string,
            state: 'active' | 'archived' | 'deleted' | 'accepted' | 'declined',
            ephemeralIdentityId?: string,
        ) => {
            try {
                return await wasmUpdateRecipientState(sendId, state, ephemeralIdentityId);
            } catch (e) {
                return { success: false, error: e instanceof Error ? e.message : String(e) };
            }
        },
        []
    );

    const claimSendAccessFn = useCallback(
        async (sendId: string, fragment: string) => {
            try {
                return await wasmClaimSendAccess(sendId, fragment);
            } catch (e) {
                return { success: false, error: e instanceof Error ? e.message : String(e) };
            }
        },
        []
    );

    const requestEphemeralEmailVerificationFn = useCallback(
        async (sendId: string, ephemeralIdentityId: string) => {
            try {
                return await wasmRequestEphemeralEmailVerification(sendId, ephemeralIdentityId);
            } catch (e) {
                return { success: false, error: e instanceof Error ? e.message : String(e) };
            }
        },
        []
    );

    const verifyEphemeralAccessFn = useCallback(
        async (sendId: string, ephemeralIdentityId: string, code: string) => {
            try {
                return await wasmVerifyEphemeralAccess(sendId, ephemeralIdentityId, code);
            } catch (e) {
                return { success: false, error: e instanceof Error ? e.message : String(e) };
            }
        },
        []
    );

    const sendEphemeralAccessEmailFn = useCallback(
        async (args: {
            sendId: string;
            recipientEmail: string;
            accessUrl: string;
            senderName?: string;
            senderOrganization?: string;
            recipientName?: string;
            message?: string;
            expiresAt?: string;
        }) => {
            try {
                return await wasmSendEphemeralAccessEmail(args);
            } catch (e) {
                return { success: false, error: e instanceof Error ? e.message : String(e) };
            }
        },
        []
    );

    // Rotate PUK (Per-User Key) - generates new PUK and distributes to all devices
    // Rotate the user's PUK. Full ceremony — new PUK generated,
    // announced, installed into the local keyring, redistributed to
    // sibling devices — runs inside the shared device library via
    // WASM. Errors surface as a failed RotatePukResult.
    const rotatePukFn = useCallback(async (): Promise<RotatePukResult> => {
        try {
            return await wasmRotatePuk();
        } catch (e) {
            return {
                success: false,
                error: e instanceof Error ? e.message : String(e),
            };
        }
    }, []);

    // Write an E2E encrypted save. Routes through the shared device
    // library via WASM — the same path the CLI uses. Errors surface
    // as a failed WriteSaveResult.
    const writeSaveE2EFn = useCallback(async (
        identityId: string,
        itemType: string,
        plaintext: Uint8Array,
        metadata?: Record<string, any>,
        existingId?: string
    ): Promise<WriteSaveResult> => {
        try {
            return await wasmWriteSave(identityId, itemType, plaintext, metadata || null, existingId);
        } catch (e) {
            return {
                success: false,
                error: e instanceof Error ? e.message : String(e),
            };
        }
    }, []);

    // List saves for an identity + itemType. Routes through the
    // shared device library (device.ListSaves wraps backend.ListSaves)
    // via WASM. Server-side query; metadata only, no payload crypto.
    const listSavesFn = useCallback(async (args: ListSavesArgsType): Promise<ListSavesResultType> => {
        return await wasmListSaves(args);
    }, []);

    // Delete a save by id. Server-side soft-delete; no client-side
    // crypto. Routes through device.DeleteSave via WASM.
    const deleteSaveFn = useCallback(async (saveId: string): Promise<{success: boolean; error?: string}> => {
        try {
            return await wasmDeleteSave(saveId);
        } catch (e) {
            return {
                success: false,
                error: e instanceof Error ? e.message : String(e),
            };
        }
    }, []);

    // Read and decrypt an E2E encrypted save. Routes through the
    // shared device library via WASM — the same path the CLI uses.
    // Errors (device not provisioned, session locked, decrypt failure)
    // surface as a failed ReadSaveE2EResult.
    const readSaveE2EFn = useCallback(async (saveId: string): Promise<ReadSaveE2EResult> => {
        try {
            return await wasmReadSave(saveId);
        } catch (e) {
            return {
                success: false,
                error: e instanceof Error ? e.message : String(e),
            };
        }
    }, []);

    // Decrypt an encrypted item directly using the keyring (no API call)
    const decryptEncryptedItemFn = useCallback(async (encryptedItem: any): Promise<Uint8Array | null> => {
        try {
            return await wasmDecryptEncryptedItem(encryptedItem);
        } catch (error) {
            console.error('[useDevice] Error decrypting encrypted item:', error);
            return null;
        }
    }, []);

    // Encrypt data with device's AES key (for local storage)
    const encryptWithDeviceAesFn = useCallback(async (plaintext: Uint8Array): Promise<EncryptOutput> => {
        return await wasmEncryptWithDeviceAes(plaintext);
    }, []);

    // Load React state from the persisted WasmDeviceRecord. Re-runs
    // whenever saveWasmDevice fires WASM_DEVICE_SAVED_EVENT (post lazy-
    // setup ceremony) so the seedKid-gated heartbeat starts and any
    // consumer reading device.seedKid / device.userInfo sees fresh
    // values without a page reload.
    //
    // No legacy SecureKeyring path here — setupDevice (invoked via the
    // setup prompter when needed) handles legacy-import internally,
    // preserving the v1 device's seed in the new WasmDeviceRecord. By
    // the time this load runs against an existing user, the import has
    // already happened; if not, the prompter will fire on the first
    // device.* op and this load re-runs on the resulting save event.
    useEffect(() => {
        let cancelled = false

        const loadFromWasmDevice = async () => {
            try {
                const rec = await loadCurrentWasmDevice()
                if (cancelled) return

                if (!rec) {
                    // No device yet — leave seedKid/sigKid/encKid/
                    // nickname at null but flip isReady so consumers
                    // that gate on "useDevice has finished initializing"
                    // (notably useCombinedAuth's isPending → the
                    // top-level menubar) can render. Consumers that
                    // need "is the device actually provisioned" check
                    // device.seedKid instead. The first user-driven
                    // device.* op triggers the setup prompter, this
                    // effect re-runs on save, and at that point
                    // seedKid/userInfo/heartbeat all populate.
                    setIsReady(true)
                    return
                }

                setSeedKid(rec.seedKid)
                setSigKid(rec.sigKid)
                setEncKid(rec.encKid)
                setNickname(rec.label || null)
                setIsReady(true)

                // Background: fetch userInfo. If the SW is locked the
                // re-unlock prompter will fire; failures are non-fatal
                // (refreshUserInfo re-syncs on push + reconnect). Flip
                // userInfoSettled either way so useCombinedAuth's
                // isPending stops gating on "still fetching."
                wasmGetUserInfo()
                    .then(info => {
                        if (cancelled) return
                        const fullUserInfo = info as unknown as UserInfo
                        userInfoRef.current = fullUserInfo
                        setUserInfo(fullUserInfo)
                    })
                    .catch(err => {
                        console.warn('[useDevice] getUserInfo on init failed (non-fatal):', err)
                    })
                    .finally(() => {
                        if (cancelled) return
                        setUserInfoSettled(true)
                    })

                // Phase 0.3 (ENG-36 / ENG-26): the legacy auto-fire of
                // wasmPullKeyring() on every device init has been removed.
                // It was the largest single contributor to session-start
                // wire load for heavy users (~6 MB / multi-second
                // getMyPxKDistributionsPaged response per page-load).
                // Catch-up after revoke/rotate/push on another device now
                // happens lazily: the operations-layer pullOnMissDecrypt
                // path fetches just-needed chain links via the targeted
                // chain-view endpoint when content can't be decrypted
                // with what's already in the keyring. Explicit
                // pullKeyring() callers (the device-context method
                // exposed below, used by CLI-style "force sync" flows
                // and a few specific provisioning paths) continue to
                // work — only the implicit auto-fire is gone.
            } catch (reason) {
                console.error('[useDevice] load error:', reason)
            }
        }

        void loadFromWasmDevice()

        const onSaved = () => { void loadFromWasmDevice() }
        window.addEventListener(WASM_DEVICE_SAVED_EVENT, onSaved)
        return () => {
            cancelled = true
            window.removeEventListener(WASM_DEVICE_SAVED_EVENT, onSaved)
        }
    }, []);

    // Build the device object
    const device: Device = {
        isReady,
        encKid,
        sigKid,
        seedKid,
        nickname,
        userInfo,
        userInfoSettled,
        sendHeartbeat,
        refreshUserInfo,
        applyUserInfo,
        listSaves: listSavesFn,
        deleteSave: deleteSaveFn,
        createTeam,
        createUser,
        completeUserCreation,
        provePaperKey,
        verifyCreationCode,
        finalizeUserCreation,
        deleteTeam,
        addUserToTeam,
        updateMemberPermissions,
        removeMemberFromTeam,
        inviteExistingUserToTeam,
        acceptTeamInvitation,
        declineTeamInvitation,
        withdrawTeamInvitation,
        requestToJoinTeam,
        approveJoinRequest,
        denyJoinRequest,
        listTeamJoinRequests,
        inviteEmailToTeam,
        claimEmailInvitation,
        listPendingIbms,
        notificationCenterItems,
        notificationUnreadCount,
        dismissStateItem,
        listTeamPendingInvitations,
        handlePtkRotations,
        listTeams,
        listTeamMembers,
        pullKeyring,
        decryptX25519Envelope,
        provisionWithAuthorizer,
        provisionWithPairing,
        intendToAddPaymentAuthorization,
        getPaymentAuthorizationPdf,
        addPaymentMethod,
        storeEphemeralIdentity,
        getUserInfo,
        getAdminStatus,
        adminListSubscriptions,
        adminProvisionSubscription,
        adminAssignLicense,
        adminUnassignLicense,
        adminSearchUsers,
        adminGetIdentityDetail,
        adminDeleteSubscription,
        listManagedProviderProfiles,
        listAllProviderProfiles,
        hasProviderProfileManageAccess,
        hasReferralPage,
        listMyManagedProviderProfiles,
        listMyProviderProfileChanges,
        submitProviderProfileChange,
        resubmitProviderProfileChange,
        adminListProviderProfileChanges,
        adminApproveProviderProfileChange,
        adminRejectProviderProfileChange,
        reassignProviderProfileRecipient,
        adminListOpenDemand,
        adminListOnboardingRequests,
        adminListRecentAccounts,
        adminGetOnboardingRequest,
        adminMarkOnboardingFollowedUp,
        registerPushToken,
        revokePushToken,
        listMyPushTokens,
        pushPublicKey,
        adminListInterestSubmissions,
        adminApproveInterestSubmission,
        adminRejectInterestSubmission,
        adminAskStillLooking,
        recommendProvidersToSearch,
        demandItemsForIdentity,
        respondToDemandInvitation,
        resubmitDemandInterest,
        openDemandOutreachLinkAsSelf,
        submitDemandInterest,
        drainAvaStillLookingCheck,
        getDemandThread,
        sendDemandMessage,
        drainAvaStaffMessages,
        sendPatientStaffMessage,
        userBubbleForAnswer,
        shareLinkURL,
        drainAvaPending,
        setDemandMessagesRead,
        adminListUnreadDemandThreads,
        adminGetStaffTeam,
        adminDesignateStaffTeam,
        adminCreatePayer,
        adminUpdatePayer,
        adminRetirePayer,
        adminSetAvaPayerChips,
        adminImportPayers,
        adminPreviewPayerChange,
        adminPayerUsedBy,
        adminPayersByState,
        adminPayerChangeLog,
        adminPayerResearchSources,
        adminResearchPayer,
        adminRecheckPayer,
        adminResearchPayerAgain,
        adminPayerResearchRuns,
        adminPayerResearchRun,
        adminDecidePayerFinding,
        adminLeaveOutPayerResearch,
        settleStillLookingCheck,
        adminGetDemandDetail,
        adminDemandMatches,
        adminMintDemandOutreachToken,
        setProviderProfileListed,
        adminRevokeDemandOutreachToken,
        getProviderProfile,
        resolveBookLink,
        listPracticeProfiles,
        createPracticeProfile,
        updatePracticeProfile,
        deletePracticeProfile,
        setProviderProfilePractice,
        arrangePracticeMembers,
        getReferenceData,
        nppesLookup,
        adminRenderProviderPreview,
        adminMintProviderPreviewLink,
        adminRevokeProviderPreviewLink,
        adminListProviderPreviewLinks,
        // Referrals (practicemd-api docs/Referrals-Architecture.md §9): plain
        // pass-throughs that throw the server's words on refusal.
        adminListReferrers: wasmAdminListReferrers,
        adminGetReferrer: wasmAdminGetReferrer,
        adminCreateReferrer: wasmAdminCreateReferrer,
        adminUpdateReferrer: wasmAdminUpdateReferrer,
        adminCreateReferralPair: wasmAdminCreateReferralPair,
        adminSetReferralRank: wasmAdminSetReferralRank,
        adminRevokeReferralLink: wasmAdminRevokeReferralLink,
        adminDeleteReferrer: wasmAdminDeleteReferrer,
        adminRecordReferrerTouch: wasmAdminRecordReferrerTouch,
        adminSetReferrerRecruiting: wasmAdminSetReferrerRecruiting,
        adminListReferralClinicians: wasmAdminListReferralClinicians,
        adminGetReferralClinician: wasmAdminGetReferralClinician,
        adminListReferralTargets: wasmAdminListReferralTargets,
        adminGetReferralPractice: wasmAdminGetReferralPractice,
        getReferralPage: wasmGetReferralPage,
        revokeReferralPair: wasmRevokeReferralPair,
        referralShareAs: wasmReferralShareAs,
        shareReferralCode: wasmShareReferralCode,
        attachSharedCodes: wasmAttachSharedCodes,
        adminMakeReferralCode: wasmAdminMakeReferralCode,
        adminFindAccounts: wasmAdminFindAccounts,
        adminLinkReferrerAccount: wasmAdminLinkReferrerAccount,
        createProviderProfile,
        updateProviderProfile,
        deleteProviderProfile,
        uploadProviderAsset,
        startAvaSearch,
        previewAvaSearch,
        runAvaMatch,
        drainAvaRecommendations,
        advanceAvaTurn,
        sendInquiriesAndAdvance,
        buildAppointmentRequestDetails,
        suggestAvaProviders,
        bindAvaSearchToAccount,
        sendAppointmentRequestInquiries,
        listAppointmentRequests,
        makeAppointmentOffer,
        declineAppointmentOffer,
        withdrawAppointmentOffer,
        sendAppointmentMessage,
        acceptAppointmentOffer,
        recordAppointmentOutcome,
        rateProvider,
        ratePatient,
        getMyPatientReputation,
        ratingsEnabled,
        declineAppointmentRequest,
        setAppointmentMessagesRead,
        requestAppointmentForms,
        recordAppointmentFormSubmission,
        deleteAppointmentSearch,
        archiveAppointmentSearch,
        unarchiveAppointmentSearch,
        withdrawAppointmentSearch,
        getAppointmentThread,
        hasAppointmentRecipientAccess,
        hasTeamMembership,
        listMyAppointmentSearches,
        listMyRequestInquiries,
        decryptSearchState,
        getAvaTranscript,
        getSavedForm,
        listPaymentVendors,
        getPaymentVendor,
        deletePaymentVendor,
        validatePaymentVendor,
        createSquareOAuthUrl,
        completeSquareOAuth,
        listPlans,
        getMyLicenses,
        getMyUsage,
        subscribeMe,
        changeSubscriptionPlan,
        cancelSubscription,
        reactivateSubscription,
        createFormTemplate,
        updateFormTemplate,
        updateFormTemplateName,
        updateFormTemplateMarks,
        updateFormTemplatePDF,
        updateFormTemplateRecipient,
        updateFormTemplateAllowAnonymous,
        bindAnonymousFormSubmission,
        deleteFormTemplate,
        claimFormTemplate,
        listFormTemplates,
        readFormTemplatePdf,
        assignPaymentIntegrationToTemplate,
        writeSend,
        writeSendMulti,
        readSend: readSendE2E,
        readSendAnonymous,
        claimSendAccess: claimSendAccessFn,
        updateRecipientState: updateRecipientStateFn,
        requestEphemeralEmailVerification: requestEphemeralEmailVerificationFn,
        verifyEphemeralAccess: verifyEphemeralAccessFn,
        sendEphemeralAccessEmail: sendEphemeralAccessEmailFn,
        listSends: listSendsFn,
        getIdentity: getIdentityFn,
        getIdentities: getIdentitiesFn,
        writeSaveE2E: writeSaveE2EFn,
        readSaveE2E: readSaveE2EFn,
        decryptEncryptedItem: decryptEncryptedItemFn,
        encryptWithDeviceAes: encryptWithDeviceAesFn,
        rotatePuk: rotatePukFn,
        listDevices,
        revokeDevices,
        pushKeys,
        checkPairingStatus,
        approvePairing,
        generatePaperKey,
        preparePaperKey,
        registerPaperKey,
        prepareRecoveryDevice,
        wrapRecoverySeedForPasskey,
        unwrapRecoverySeedWithPasskey,
        registerPasskeyDevice,
        recoveryEnvelope,
        updateUserinfo,
        lastSeenAt: lastSeenAtRef.current,
    };

    return device;
}

// Context for singleton device instance
export const DeviceContext = React.createContext<Device | null>(null);

/**
 * DeviceProvider - Wrap your app with this to create a singleton device instance
 *
 * Usage:
 *   <DeviceProvider>
 *     <App />
 *   </DeviceProvider>
 */
export const DeviceProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const device = useDevice();
    // Latest device, readable from event listeners that bind once on
    // mount (the push OOBM consumer below) without re-binding on every
    // userInfo change.
    const deviceRef = useRef(device);
    deviceRef.current = device;

    // Expose the provided device on window for e2e tests to drive ops
    // (write/read/list/delete save, etc.) directly against the same
    // implementation consumers use, rather than grinding through UI
    // selectors. Same object that useDeviceContext() returns.
    useEffect(() => {
        if (typeof window !== 'undefined') {
            (window as any).__practicemdDevice = device;
        }
    });
    // Test-only hook: expose the low-level setupDevice ceremony so
    // e2e specs can mint a fresh persisted device (the same thing
    // the boot-gate will do from UI once Step 3 lands) in an
    // anonymous browser context without grinding through UI
    // selectors. Real users never touch this path.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        import('../../wasm/DeviceUnlockSetup').then(mod => {
            (window as any).__practicemdTest = { setupDevice: mod.setupDevice };
        }).catch(() => {});
    }, []);

    // Push socket. Once the device is active (seedKid set, i.e. unlocked
    // with an identity), open the origin's single push socket; leader
    // election in pushConnection ensures one socket across tabs, with
    // inbound frames delivered as PUSH_FRAME_EVENT. Gated on seedKid the
    // same way the heartbeat is; cleanup stops our hold on the socket.
    useEffect(() => {
        if (!device.seedKid) return;
        const stop = startPush();
        return () => stop();
    }, [device.seedKid]);

    // Push OOBM consumer. The socket above re-dispatches every inbound frame
    // as PUSH_FRAME_EVENT; this hands each one to the SHARED routing policy
    // (device.ConsumePushFrame) and applies whatever it reports back.
    //
    // The routing used to live here, inline: which oobm types matter, which
    // gates are evaluated in which order, and two rules that are the
    // resolution of production incidents — the ENG-55 skip that keeps
    // appointment-marketplace IBMs from pulling the keyring, and (in
    // catch-up below) the ENG-36 staleness gate. None of that was visible to
    // a second client, so the CLI could not have agreed with it; it now
    // cannot disagree.
    //
    // What stays here is what is genuinely this client's: dispatching the
    // window event, and adopting the returned UserInfo into React state. The
    // library reports rather than mutates, because Device holds no UserInfo.
    // Bound once; reads the live device via deviceRef.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onFrame = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            if (!detail || !detail.oobm) return;
            void (async () => {
                try {
                    const out = await consumePushFrame(
                        deviceRef.current.userInfo?.id ?? "",
                        detail
                    );
                    if (out.userInfo) deviceRef.current.applyUserInfo(out.userInfo as unknown as UserInfo);
                } catch {
                    // A bad frame must never tear down the listener; the
                    // authoritative state is the server's, and reconnect
                    // catch-up re-converges.
                }
            })();
        };
        window.addEventListener(PUSH_FRAME_EVENT, onFrame);
        return () => window.removeEventListener(PUSH_FRAME_EVENT, onFrame);
    }, []);

    // Reconnect catch-up (§16.5). The push socket fires PUSH_CONNECTED_EVENT
    // (leader-local) on every (re)establishment, the first of which is this
    // page load. OOBMs missed while disconnected are dropped, not replayed
    // (§8 — the keyring is the authoritative store), so a client converges
    // here rather than waiting for a frame that will not come.
    //
    // The sequence — always refresh userInfo, pull the keyring ONLY when the
    // server's head is not already held, then run the PTK rotation backstop —
    // is device.CatchUpAfterConnect. It moved there because the middle step is
    // the ENG-36 gate: an unconditional pull re-introduces the eager
    // session-start keyring fetch that was deliberately removed.
    //
    // The event fires only in the leader tab and the keyring is origin-shared,
    // so this converges every tab. Bound once.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onConnected = () => {
            void (async () => {
                try {
                    const out = await catchUpAfterConnect();
                    if (out.userInfo) deviceRef.current.applyUserInfo(out.userInfo as unknown as UserInfo);
                } catch {
                    /* next reconnect retries; nothing here is authoritative */
                }
            })();
        };
        window.addEventListener(PUSH_CONNECTED_EVENT, onConnected);
        return () => window.removeEventListener(PUSH_CONNECTED_EVENT, onConnected);
    }, []);

    // Global re-unlock handling. prepareDeviceArgs (in wasm/ops.ts)
    // calls the prompter whenever an op runs against a locked SW;
    // this provider is the one place that owns the modal and the
    // pending-promise state. Any device.* method anywhere in the app
    // that hits a locked SW transparently prompts the user and
    // retries — callers don't need to know.
    const [showUnlockDialog, setShowUnlockDialog] = useState(false);
    const pendingUnlockRef = useRef<{
        resolve: () => void;
        reject: (e: Error) => void;
    } | null>(null);

    useEffect(() => {
        const prompter = () => new Promise<void>((resolve, reject) => {
            // Engineering pages under /spike/ run WebAuthn ceremonies of
            // their own, and a page can run only one at a time — so the
            // product's unlock prompt (which auto-starts the passkey) must
            // not be raised over them. Same outcome as the person pressing
            // Cancel: the op that needed the device fails, nothing hangs.
            if (typeof window !== "undefined" && window.location.pathname.startsWith("/spike/")) {
                reject(new Error("re-unlock cancelled"));
                return;
            }
            // If something is already waiting, attach this caller to
            // the same resolution — the dialog is a singleton.
            const prior = pendingUnlockRef.current;
            pendingUnlockRef.current = {
                resolve: () => { prior?.resolve(); resolve(); },
                reject: (e) => { prior?.reject(e); reject(e); },
            };
            setShowUnlockDialog(true);
        });
        setUnlockPrompter(prompter);
        return () => setUnlockPrompter(null);
    }, []);

    const handleUnlockDone = useCallback(() => {
        setShowUnlockDialog(false);
        const pending = pendingUnlockRef.current;
        pendingUnlockRef.current = null;
        pending?.resolve();
    }, []);

    const handleUnlockCancel = useCallback(() => {
        setShowUnlockDialog(false);
        const pending = pendingUnlockRef.current;
        pendingUnlockRef.current = null;
        pending?.reject(new Error('re-unlock cancelled'));
    }, []);

    // Global device-setup handling. Mirrors the re-unlock prompter:
    // when prepareDeviceArgs sees no WasmDeviceRecord, it awaits the
    // registered setup prompter, which surfaces the setup ceremony
    // here. The first device.* call in any flow transparently triggers
    // setup — no route-level preflight needed.
    const [showSetupDialog, setShowSetupDialog] = useState(false);
    const pendingSetupRef = useRef<{
        resolve: () => void;
        reject: (e: Error) => void;
    } | null>(null);

    useEffect(() => {
        const prompter = () => new Promise<void>((resolve, reject) => {
            const prior = pendingSetupRef.current;
            pendingSetupRef.current = {
                resolve: () => { prior?.resolve(); resolve(); },
                reject: (e) => { prior?.reject(e); reject(e); },
            };
            setShowSetupDialog(true);
        });
        setSetupPrompter(prompter);
        return () => setSetupPrompter(null);
    }, []);

    // Appendix C (C5): the overlay no longer sets a device up; it sends a
    // cold browser to sign in. Dismissing it — or leaving for /authorize —
    // rejects the op that raised it, so nothing awaits forever.
    const handleSetupDismiss = useCallback(() => {
        setShowSetupDialog(false);
        const pending = pendingSetupRef.current;
        pendingSetupRef.current = null;
        pending?.reject(new NoWasmDeviceError());
    }, []);

    // The setup/unlock dialogs are PORTALED to document.body. Gatsby v5's Head
    // API re-renders wrapRootElement (this DeviceProvider) to wrap each page's
    // `Head` export, and `setSetupPrompter`/`setUnlockPrompter` are module-level
    // singletons (last registrant wins) — so the Head's provider instance can
    // become the active prompter and render the dialog inline INTO <head>, where
    // it's invisible and the awaiting device op hangs (this is exactly the /ava
    // blank-screen bug). Portaling to body makes the dialog visible regardless of
    // which provider instance triggers it. SSR-guarded: document is undefined
    // during server render, and these only ever open client-side.
    const canPortal = typeof document !== "undefined";

    // STABLE IDENTITY for the context value. The literal above is rebuilt every
    // provider render, and handing consumers a fresh object each time re-fired
    // every effect with `device` in its deps on every render — on the public
    // interest page that meant repeated openDemandOutreachLink calls, each one
    // CHARGING the token's view budget and re-claiming the one-shot parked
    // draft. Every member is either state or a useCallback, so a shallow
    // compare is exact: identity changes iff something actually changed.
    const stableDeviceRef = useRef<Device>(device);
    {
        const prev = stableDeviceRef.current as unknown as Record<string, unknown>;
        const next = device as unknown as Record<string, unknown>;
        let changed = false;
        for (const k in next) {
            if (prev[k] !== next[k]) {
                changed = true;
                break;
            }
        }
        if (changed) stableDeviceRef.current = device;
    }

    return (
        <DeviceContext.Provider value={stableDeviceRef.current}>
            {children}
            {showUnlockDialog && canPortal && createPortal(
                <ReUnlockDialog onDone={handleUnlockDone} onCancel={handleUnlockCancel} />,
                document.body,
            )}
            {showSetupDialog && canPortal && createPortal(
                <DeviceSetupOverlay onDismiss={handleSetupDismiss} />,
                document.body,
            )}
        </DeviceContext.Provider>
    );
};

/**
 * Default device state for SSR or when DeviceProvider is not available
 */
const defaultDevice: Device = {
    isReady: false,
    seedKid: null,
    sigKid: null,
    encKid: null,
    nickname: null,
    userInfo: null,
    userInfoSettled: false,
    lastSeenAt: null,
    sendHeartbeat: async () => {},
    refreshUserInfo: async () => null,
    applyUserInfo: () => {},
    listSaves: async () => ({ items: [], nextCursor: '', totalCount: 0 }),
    deleteSave: async () => ({ success: false, error: 'Device not ready' }),
    createTeam: async () => ({ success: false, error: 'Device not ready' }),
    createUser: async () => ({ success: false, verificationSent: false, error: 'Device not ready' }),
    completeUserCreation: async () => ({ success: false, error: 'Device not ready' }),
    provePaperKey: async () => ({ success: false, error: 'Device not ready' }),
    verifyCreationCode: async () => ({ success: false, error: 'Device not ready' }),
    finalizeUserCreation: async () => ({ success: false, error: 'Device not ready' }),
    deleteTeam: async () => ({ success: false, error: 'Device not ready' }),
    addUserToTeam: async () => ({ success: false, error: 'Device not ready' }),
    updateMemberPermissions: async () => ({ success: false, error: 'Device not ready' }),
    removeMemberFromTeam: async () => ({ success: false, error: 'Device not ready' }),
    inviteExistingUserToTeam: async () => ({ success: false, error: 'Device not ready' }),
    acceptTeamInvitation: async () => ({ success: false, error: 'Device not ready' }),
    declineTeamInvitation: async () => ({ success: false, error: 'Device not ready' }),
    withdrawTeamInvitation: async () => ({ success: false, error: 'Device not ready' }),
    requestToJoinTeam: async () => ({ success: false, error: 'Device not ready' }),
    approveJoinRequest: async () => ({ success: false, error: 'Device not ready' }),
    denyJoinRequest: async () => ({ success: false, error: 'Device not ready' }),
    listTeamJoinRequests: async () => [],
    inviteEmailToTeam: async () => ({ success: false, error: 'Device not ready' }),
    claimEmailInvitation: async () => ({ success: false, error: 'Device not ready' }),
    listPendingIbms: async () => [],
    notificationCenterItems: async () => [],
    notificationUnreadCount: async () => 0,
    dismissStateItem: async () => false,
    listTeamPendingInvitations: async () => [],
    handlePtkRotations: async () => ({ handled: 0, error: 'Device not ready' }),
    listTeams: async () => [],
    listTeamMembers: async () => [],
    pullKeyring: async () => ({ added: 0, skipped: 0, total: 0 }),
    decryptX25519Envelope: async () => { throw new Error('Device not ready'); },
    provisionWithAuthorizer: async () => ({ success: false, error: 'Device not ready' }),
    provisionWithPairing: async () => ({ success: false, error: 'Device not ready' }),
    intendToAddPaymentAuthorization: async () => ({ available: false, hasAgreementOnFile: false, hasCardOnFile: false, error: { message: 'Device not ready' } }),
    getPaymentAuthorizationPdf: async () => { throw new Error('Device not ready'); },
    addPaymentMethod: async () => ({ hasAgreementOnFile: false, hasCardOnFile: false, error: { message: 'Device not ready' } }),
    storeEphemeralIdentity: async () => { throw new Error('Device not ready'); },
    getUserInfo: async () => { throw new Error('Device not ready'); },
    getAdminStatus: async () => ({ isAdmin: false }),
    adminListSubscriptions: async () => [],
    adminProvisionSubscription: async () => ({ success: false, error: 'Device not ready' }),
    adminAssignLicense: async () => ({ success: false, error: 'Device not ready' }),
    adminUnassignLicense: async () => ({ success: false, error: 'Device not ready' }),
    adminSearchUsers: async () => [],
    adminGetIdentityDetail: async (id: string) => ({ id, kind: '', name: '', email: '' }),
    adminDeleteSubscription: async () => ({ success: false, error: 'Device not ready' }),
    listManagedProviderProfiles: async () => [],
    listAllProviderProfiles: async () => [],
    hasProviderProfileManageAccess: async () => false,
    hasReferralPage: async () => false,
    listMyManagedProviderProfiles: async () => ({ profiles: [], identities: [] }),
    listMyProviderProfileChanges: async () => [],
    submitProviderProfileChange: async () => { throw new Error("no device"); },
    resubmitProviderProfileChange: async () => { throw new Error("no device"); },
    adminListProviderProfileChanges: async () => [],
    adminApproveProviderProfileChange: async () => { throw new Error("no device"); },
    adminRejectProviderProfileChange: async () => { throw new Error("no device"); },
    reassignProviderProfileRecipient: async () => { throw new Error("no device"); },
    adminListOpenDemand: async () => [],
    // The onboarding queue. Empty and a blank document rather than a throw: this
    // stub is what SSR and any tree without a DeviceProvider get, and a throw
    // there would break a render that was never going to show staff data anyway.
    adminListOnboardingRequests: async () => [],
    adminListRecentAccounts: async () => [],
    adminGetOnboardingRequest: async () => '',
    // A write, so it refuses rather than pretending: nothing may read as
    // followed up when no device ever recorded it.
    adminMarkOnboardingFollowedUp: async () => { throw new Error("no device"); },
    // Web Push. Writes refuse; reads answer "nothing here", so an SSR render
    // shows the control as unavailable rather than breaking.
    registerPushToken: async () => { throw new Error("no device"); },
    revokePushToken: async () => { throw new Error("no device"); },
    listMyPushTokens: async () => [],
    pushPublicKey: async () => '',
    adminListInterestSubmissions: async () => [],
    adminApproveInterestSubmission: async () => ({ success: false, error: 'Device not ready' }),
    adminRejectInterestSubmission: async () => {},
    adminAskStillLooking: async () => ({ success: false, error: 'Device not ready' }),
    recommendProvidersToSearch: async () => ({ success: false, error: 'Device not ready' }),
    demandItemsForIdentity: async () => [],
    respondToDemandInvitation: async () => ({ ok: false, error: 'Device not ready' }),
    resubmitDemandInterest: async () => ({ ok: false, error: 'Device not ready' }),
    openDemandOutreachLinkAsSelf: async () => ({ ok: false, error: 'Device not ready' }),
    submitDemandInterest: async () => ({ ok: false, error: 'Device not ready' }),
    drainAvaStillLookingCheck: async () => ({ rendered: false, deferred: false, nextSeq: 0 }),
    settleStillLookingCheck: async () => false,
    getDemandThread: async () => null,
    sendDemandMessage: async () => ({ messageId: '', sendId: '' }),
    drainAvaStaffMessages: async () => ({ turns: [], nextSeq: 0 }),
    sendPatientStaffMessage: async () => ({ messageId: '', sendId: '', turn: {}, nextSeq: 0 }),
    setDemandMessagesRead: async () => 0,
    adminListUnreadDemandThreads: async () => [],
    adminGetStaffTeam: async () => ({ teamId: '', teamName: '' }),
    adminDesignateStaffTeam: async () => ({ teamId: '', teamName: '' }),
    adminCreatePayer: async () => ({ ok: false, error: 'the device is not ready' }),
    adminUpdatePayer: async () => ({ ok: false, error: 'the device is not ready' }),
    adminRetirePayer: async () => ({ ok: false, error: 'the device is not ready' }),
    adminSetAvaPayerChips: async () => ({ ok: false, error: 'the device is not ready' }),
    adminImportPayers: async () => ({ ok: false, error: 'the device is not ready' }),
    adminPreviewPayerChange: async () => ({ ok: false, error: 'the device is not ready' }),
    adminPayerUsedBy: async () => ({ ok: false, error: 'the device is not ready' }),
    adminPayersByState: async () => ({ ok: false, error: 'the device is not ready' }),
    adminPayerChangeLog: async () => ({ ok: false, error: 'the device is not ready' }),
    adminPayerResearchSources: async () => ({ ok: false, error: 'the device is not ready' }),
    adminResearchPayer: async () => ({ ok: false, error: 'the device is not ready' }),
    adminRecheckPayer: async () => ({ ok: false, error: 'the device is not ready' }),
    adminResearchPayerAgain: async () => ({ ok: false, error: 'the device is not ready' }),
    adminPayerResearchRuns: async () => ({ ok: false, error: 'the device is not ready' }),
    adminPayerResearchRun: async () => ({ ok: false, error: 'the device is not ready' }),
    adminDecidePayerFinding: async () => ({ ok: false, error: 'the device is not ready' }),
    adminLeaveOutPayerResearch: async () => ({ ok: false, error: 'the device is not ready' }),
    adminGetDemandDetail: async () => null,
    adminDemandMatches: async () => [],
    adminMintDemandOutreachToken: async () => ({ success: false, error: 'Device not ready' }),
    setProviderProfileListed: async () => false,
    adminRevokeDemandOutreachToken: async () => {},
    getProviderProfile: async () => null,
    resolveBookLink: async () => ({ kind: 'none' as const }),
    listPracticeProfiles: async () => [],
    createPracticeProfile: async () => { throw new Error('Device not ready'); },
    updatePracticeProfile: async () => { throw new Error('Device not ready'); },
    deletePracticeProfile: async () => false,
    setProviderProfilePractice: async () => { throw new Error('Device not ready'); },
    arrangePracticeMembers: async () => { throw new Error('Device not ready'); },
    getReferenceData: async () => { throw new Error('device not ready'); },
    nppesLookup: async () => ({ success: false, error: 'device not ready', found: false, taxonomies: [] }),
    adminRenderProviderPreview: async () => ({ success: false, error: 'device not ready', pages: [] }),
    adminMintProviderPreviewLink: async () => ({ success: false, error: 'device not ready' }),
    adminRevokeProviderPreviewLink: async () => {},
    adminListProviderPreviewLinks: async () => [],
    adminListReferrers: async () => { throw new Error('device not ready'); },
    adminGetReferrer: async () => { throw new Error('device not ready'); },
    adminCreateReferrer: async () => { throw new Error('device not ready'); },
    adminUpdateReferrer: async () => { throw new Error('device not ready'); },
    adminCreateReferralPair: async () => { throw new Error('device not ready'); },
    adminSetReferralRank: async () => { throw new Error('device not ready'); },
    adminRevokeReferralLink: async () => { throw new Error('device not ready'); },
    adminDeleteReferrer: async () => { throw new Error('device not ready'); },
    adminRecordReferrerTouch: async () => { throw new Error('device not ready'); },
    adminSetReferrerRecruiting: async () => { throw new Error('device not ready'); },
    adminListReferralClinicians: async () => { throw new Error('device not ready'); },
    adminGetReferralClinician: async () => { throw new Error('device not ready'); },
    adminListReferralTargets: async () => { throw new Error('device not ready'); },
    adminGetReferralPractice: async () => { throw new Error('device not ready'); },
    getReferralPage: async () => { throw new Error('device not ready'); },
    revokeReferralPair: async () => { throw new Error('device not ready'); },
    referralShareAs: async () => { throw new Error('device not ready'); },
    shareReferralCode: async () => { throw new Error('device not ready'); },
    attachSharedCodes: async () => { throw new Error('device not ready'); },
    adminMakeReferralCode: async () => { throw new Error('device not ready'); },
    adminFindAccounts: async () => { throw new Error('device not ready'); },
    adminLinkReferrerAccount: async () => { throw new Error('device not ready'); },
    createProviderProfile: async () => ({ error: 'Device not ready' }),
    updateProviderProfile: async () => ({ error: 'Device not ready' }),
    deleteProviderProfile: async () => ({ success: false, error: 'Device not ready' }),
    uploadProviderAsset: async () => ({ url: '' }),
    startAvaSearch: async () => { throw new Error('Device not ready'); },
    previewAvaSearch: async () => { throw new Error('Device not ready'); },
    runAvaMatch: async () => { throw new Error('Device not ready'); },
    // Inert rather than throwing: a drain is a "surface anything queued" nudge that
    // fires on open and on push, so a not-ready device should be a no-op, not an error
    // the caller has to catch.
    drainAvaRecommendations: async () => ({
        drained: 0, providers: [], origin: '', deferred: false,
        stateSnapshot: {}, nextSeq: 0,
    }),
    advanceAvaTurn: async () => { throw new Error('Device not ready'); },
    sendInquiriesAndAdvance: async () => { throw new Error('Device not ready'); },
    userBubbleForAnswer: async () => { throw new Error('Device not ready'); },
    shareLinkURL: async () => { throw new Error('Device not ready'); },
    drainAvaPending: async () => { throw new Error('Device not ready'); },
    buildAppointmentRequestDetails: async () => { throw new Error('Device not ready'); },
    suggestAvaProviders: async () => ({ providers: [], stateSnapshot: {} }),
    bindAvaSearchToAccount: async () => { throw new Error('Device not ready'); },
    sendAppointmentRequestInquiries: async () => { throw new Error('Device not ready'); },
    listAppointmentRequests: async () => [],
    makeAppointmentOffer: async () => { throw new Error('Device not ready'); },
    acceptAppointmentOffer: async () => { throw new Error('Device not ready'); },
    recordAppointmentOutcome: async () => { throw new Error('Device not ready'); },
    rateProvider: async () => { throw new Error('Device not ready'); },
    ratePatient: async () => { throw new Error('Device not ready'); },
    getMyPatientReputation: async () => { throw new Error('Device not ready'); },
    ratingsEnabled: async () => { throw new Error('Device not ready'); },
    declineAppointmentRequest: async () => { throw new Error('Device not ready'); },
    setAppointmentMessagesRead: async () => { throw new Error('Device not ready'); },
    requestAppointmentForms: async () => { throw new Error('Device not ready'); },
    recordAppointmentFormSubmission: async () => { throw new Error('Device not ready'); },
    declineAppointmentOffer: async () => { throw new Error('Device not ready'); },
    withdrawAppointmentOffer: async () => { throw new Error('Device not ready'); },
    sendAppointmentMessage: async () => { throw new Error('Device not ready'); },
    deleteAppointmentSearch: async () => { throw new Error('Device not ready'); },
    archiveAppointmentSearch: async () => { throw new Error('Device not ready'); },
    unarchiveAppointmentSearch: async () => { throw new Error('Device not ready'); },
    withdrawAppointmentSearch: async () => { throw new Error('Device not ready'); },
    getAppointmentThread: async () => null,
    hasAppointmentRecipientAccess: async () => false,
    hasTeamMembership: async () => false,
    listMyAppointmentSearches: async () => [],
    listMyRequestInquiries: async () => [],
    decryptSearchState: async () => ({}),
    getAvaTranscript: async () => ({ entries: [], state: {} }),
    getSavedForm: async () => ({ success: false, error: 'Device not ready' }),
    listPaymentVendors: async () => [],
    getPaymentVendor: async () => null,
    deletePaymentVendor: async () => ({ success: false, error: 'Device not ready' }),
    validatePaymentVendor: async () => ({ success: false, error: 'Device not ready' }),
    createSquareOAuthUrl: async () => ({ success: false, error: 'Device not ready' }),
    completeSquareOAuth: async () => ({ success: false, error: 'Device not ready' }),
    listPlans: async () => [],
    getMyLicenses: async () => [],
    getMyUsage: async () => ({}),
    subscribeMe: async () => ({}),
    changeSubscriptionPlan: async () => ({}),
    cancelSubscription: async () => ({}),
    reactivateSubscription: async () => ({}),
    createFormTemplate: async () => ({ success: false, error: 'Device not ready', claimed: false, limitExceeded: false, limitMessage: '', upgradeCTA: '' }),
    updateFormTemplate: async () => ({ success: false, error: 'Device not ready' }),
    updateFormTemplateName: async () => ({ success: false, error: 'Device not ready' }),
    updateFormTemplateMarks: async () => ({ success: false, error: 'Device not ready' }),
    updateFormTemplatePDF: async () => ({ success: false, error: 'Device not ready' }),
    updateFormTemplateRecipient: async () => ({ success: false, error: 'Device not ready' }),
    updateFormTemplateAllowAnonymous: async () => ({ success: false, error: 'Device not ready' }),
    bindAnonymousFormSubmission: async () => ({ success: false, error: 'Device not ready' }),
    deleteFormTemplate: async () => ({ success: false, error: 'Device not ready' }),
    claimFormTemplate: async () => ({ success: false, error: 'Device not ready' }),
    listFormTemplates: async () => ({ items: [], nextCursor: '', totalCount: 0 }),
    readFormTemplatePdf: async () => ({ success: false, error: 'Device not ready' }),
    assignPaymentIntegrationToTemplate: async () => ({ success: false, error: 'Device not ready' }),
    writeSend: async () => ({ success: false, error: 'Device not ready' }),
    writeSendMulti: async () => ({ success: false, error: 'Device not ready' }),
    readSend: async () => ({ success: false, error: 'Device not ready' }),
    readSendAnonymous: async () => ({ success: false, error: 'Device not ready' }),
    claimSendAccess: async () => ({ success: false, error: 'Device not ready' }),
    updateRecipientState: async () => ({ success: false, error: 'Device not ready' }),
    requestEphemeralEmailVerification: async () => ({ success: false, error: 'Device not ready' }),
    verifyEphemeralAccess: async () => ({ success: false, error: 'Device not ready' }),
    sendEphemeralAccessEmail: async () => ({ success: false, error: 'Device not ready' }),
    listSends: async () => ({ items: [], nextCursor: '', totalCount: 0 }),
    getIdentity: async () => { throw new Error('Device not ready'); },
    getIdentities: async () => { throw new Error('Device not ready'); },
    writeSaveE2E: async () => ({ success: false, error: 'Device not ready' }),
    readSaveE2E: async () => ({ success: false, error: 'Device not ready' }),
    decryptEncryptedItem: async () => null,
    encryptWithDeviceAes: async () => { throw new Error('Device not ready'); },
    rotatePuk: async () => ({ success: false, error: 'Device not ready' }),
    listDevices: async () => [],
    revokeDevices: async () => ({ success: false, error: 'Device not ready' }),
    pushKeys: async () => ({ success: false, error: 'Device not ready' }),
    checkPairingStatus: async () => ({ status: '', isCompleted: false, expiresIn: 0, error: 'Device not ready' }),
    approvePairing: async () => ({ success: false, error: 'Device not ready' }),
    generatePaperKey: async () => ({success: false, error: 'Device not ready'}),
    preparePaperKey: async () => ({success: false, error: 'Device not ready'}),
    registerPaperKey: async () => ({success: false, error: 'Device not ready'}),
    prepareRecoveryDevice: async () => ({success: false, error: 'Device not ready'}),
    wrapRecoverySeedForPasskey: async () => { throw new Error('Device not ready') },
    unwrapRecoverySeedWithPasskey: async () => { throw new Error('Device not ready') },
    registerPasskeyDevice: async () => ({success: false, error: 'Device not ready'}),
    recoveryEnvelope: async () => ({found: false}),
    updateUserinfo: async () => ({success: false, error: 'Device not ready'}),
};

/**
 * useDeviceContext - Access the singleton device instance
 *
 * Must be used within a DeviceProvider. Use this instead of useDevice() directly
 * to ensure all components share the same device instance.
 *
 * During SSR or when outside DeviceProvider, returns a default "not ready" device.
 */
export const useDeviceContext = (): Device => {
    const context = useContext(DeviceContext);
    // During SSR or when DeviceProvider is not available, return default device
    if (!context) {
        return defaultDevice;
    }
    return context;
};
